import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Printer, FileDown, FileText, Pencil, XCircle } from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Document, Packer, Paragraph, TextRun, AlignmentType, BorderStyle, Table, TableRow, TableCell, WidthType, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import TrDateInput from '../components/TrDateInput';
import { useTranslation } from 'react-i18next';

interface KiraOdeme {
  odemeID: number;
  cariID: number;
  islemTarihi: string;
  vadeTarihi?: string;
  tahsilatTarihi?: string;
  miktar: number;
  paraBirimiID: number;
  islemTakipNo: string;
  odemeTuru: string;
  odemeYonu: string;
  odemeDurumu: string;
  aciklama?: string;
  aktifMi: boolean;
  kiraSozlesmesiOdemesi: boolean;
  eklemeTarihi: string;
  kasaID?: number;
  paraBirimi: { paraBirimiID: number; birim: string };
  kasa?: { kasaID: number; kasaAdi: string };
}

interface ExsikAy {
  yil: number;
  ay: number;
  vadeTarihi: string;
  miktar: number;
}

interface KiraSozlesmesi {
  kiraSozlesmeID: number;
  takipNo: string;
  gayrimenkulID: number;
  cariID: number;
  baslangicTarihi: string;
  bitisTarihi: string;
  kiraBedeli: number;
  odemeGunu: number;
  kapora: number;
  ekKosullar?: string;
  aktifMi: boolean;
  gayrimenkul?: {
    gayrimenkulID: number;
    takipNo: string;
    kategori: number;
    baslik: string;
    adres: string;
    metrekare: number;
    kat: string | null;
    durum: number;
    projeID: number | null;
    aktifMi: boolean;
    proje?: { projeID: number; projeAdi: string } | null;
  } | null;
  cari?: {
    cariID: number;
    takipNo: string;
    adiSoyadi: string;
    firmaAdi?: string;
    telefon1: string;
    telefon2?: string;
    ePosta?: string;
    adres?: string;
    tc?: string;
    cariVD?: string;
    cariVN?: string;
    turu: string;
  } | null;
}

const KATEGORI_MAP_KEYS: Record<number, string> = { 0: 'kira.daire', 1: 'kira.ofis', 2: 'kira.dukkan' };
const DURUM_MAP: Record<number, string> = { 0: 'Boş', 1: 'Kirada', 2: 'Bakımda' };

const KiraSozlesmesiDetay: React.FC = () => {
  const { takipNo } = useParams<{ takipNo: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const KATEGORI_MAP: Record<number, string> = {
    0: t('kira.daire'), 1: t('kira.ofis'), 2: t('kira.dukkan')
  };
  const printPaperRef = useRef<HTMLDivElement>(null);

  const handlePdfIndir = async () => {
    if (!printPaperRef.current || !sozlesme) return;
    const toastId = toast.loading(t('kira.pdfOlusturuluyor'));
    try {
      const el = printPaperRef.current;
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const ratio = canvas.width / canvas.height;
      const imgH = pdfW / ratio;
      let yPos = 0;
      let remaining = imgH;
      while (remaining > 0) {
        pdf.addImage(imgData, 'PNG', 0, -yPos, pdfW, imgH);
        remaining -= pdfH;
        if (remaining > 0) { pdf.addPage(); yPos += pdfH; }
      }
      pdf.save(`KiraSozlesmesi-${sozlesme.takipNo}.pdf`);
      toast.update(toastId, { render: t('kira.pdfIndirildi'), type: 'success', isLoading: false, autoClose: 2500 });
    } catch (e) {
      toast.update(toastId, { render: t('kira.pdfOlusturulamadi'), type: 'error', isLoading: false, autoClose: 3000 });
    }
  };
  const [sozlesme, setSozlesme] = useState<KiraSozlesmesi | null>(null);
  const [loading, setLoading] = useState(true);
  const [showIptalModal, setShowIptalModal] = useState(false);
  const [iptalStep, setIptalStep] = useState<1 | 2>(1);
  const [iptalBekleyenOdemeler, setIptalBekleyenOdemeler] = useState<KiraOdeme[]>([]);
  const [iptalSeciliOdemeler, setIptalSeciliOdemeler] = useState<Set<number>>(new Set());
  const [iptalSaving, setIptalSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [showIslemlerMenu, setShowIslemlerMenu] = useState(false);
  const [showSozlesmeIndirModal, setShowSozlesmeIndirModal] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [pendingAutoDownload, setPendingAutoDownload] = useState(false);

  useEffect(() => {
    if (showPrintPreview && pendingAutoDownload) {
      const timer = setTimeout(() => {
        handlePdfIndir().finally(() => setPendingAutoDownload(false));
      }, 600);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPrintPreview, pendingAutoDownload]);
  const [editForm, setEditForm] = useState({
    baslangicTarihi: '',
    bitisTarihi: '',
    kiraBedeli: 0,
    odemeGunu: 1,
    kapora: 0,
    ekKosullar: ''
  });
  
  // Döküman Şablonları
  const [dokumanSablonlari, setDokumanSablonlari] = useState<any[]>([]);
  const [showSablonModal, setShowSablonModal] = useState(false);

  // Ödeme state'leri
  const [odemeler, setOdemeler] = useState<KiraOdeme[]>([]);
  const [odemelerLoading, setOdemelerLoading] = useState(false);
  const [kasalar, setKasalar] = useState<any[]>([]);
  const [eksikAylar, setEksikAylar] = useState<ExsikAy[]>([]);
  const [showEksikUyariModal, setShowEksikUyariModal] = useState(false);
  const [showOdemeModal, setShowOdemeModal] = useState(false);
  const [showEditOdemeModal, setShowEditOdemeModal] = useState(false);
  const [editingOdeme, setEditingOdeme] = useState<KiraOdeme | null>(null);
  const [odemeSaving, setOdemeSaving] = useState(false);
  const [odemeForm, setOdemeForm] = useState({
    vadeTarihi: '',
    miktar: '',
    odemeTuru: 'Nakit',
    odemeDurumu: 'Bekliyor',
    kasaID: '',
    aciklama: ''
  });
  const [editOdemeForm, setEditOdemeForm] = useState({
    odemeDurumu: '',
    tahsilatTarihi: '',
    kasaID: '',
    aciklama: ''
  });

  // Toplu Ödeme Planla modal
  const [showTopluPlanlaModal, setShowTopluPlanlaModal] = useState(false);
  const [topluPlanlaForm, setTopluPlanlaForm] = useState({
    toplamTutar: '',
    baslamaTarihi: '',
    taksitSayisi: '',
    aralikAy: '1',
    odemeTuru: 'Nakit',
    aciklama: ''
  });
  const [topluPlanlaLoading, setTopluPlanlaLoading] = useState(false);

  // Tahsilat Yap modal
  const [showTahsilatModal, setShowTahsilatModal] = useState(false);
  const [tahsilatTaksit, setTahsilatTaksit] = useState<KiraOdeme | null>(null);
  const [tahsilatForm, setTahsilatForm] = useState({
    tarih: '',
    kasaID: '',
    odemeTuru: 'Nakit',
    aciklama: ''
  });
  const [tahsilatLoading, setTahsilatLoading] = useState(false);

  useEffect(() => {
    if (takipNo) {
      fetchSozlesme();
      fetchDokumanSablonlari();
    }
  }, [takipNo]);

  useEffect(() => {
    if (takipNo) {
      fetchOdemeler(takipNo);
      fetchKasalar();
    }
  }, [takipNo]);

  // Eksik aylık ödemeleri hesapla
  const hesaplaEksikAylar = useCallback((mevcut: KiraOdeme[], s: KiraSozlesmesi): ExsikAy[] => {
    const eksik: ExsikAy[] = [];
    const baslangic = new Date(s.baslangicTarihi);
    const bitis = new Date(s.bitisTarihi);

    let current = new Date(baslangic.getFullYear(), baslangic.getMonth(), s.odemeGunu);
    // Başlangıç ayının ödeme günü başlangıç tarihinden önce düşüyorsa bir sonraki aya geç
    if (current < baslangic) {
      current = new Date(current.getFullYear(), current.getMonth() + 1, s.odemeGunu);
    }

    // Başlangıçtan bitişe kadar TÜM ayları kontrol et
    while (current <= bitis) {
      const yil = current.getFullYear();
      const ay = current.getMonth();
      const varMi = mevcut.some(o => {
        if (!o.vadeTarihi) return false;
        const vd = new Date(o.vadeTarihi);
        return vd.getFullYear() === yil && vd.getMonth() === ay;
      });
      if (!varMi) {
        const pad = (n: number) => String(n).padStart(2, '0');
        eksik.push({
          yil,
          ay,
          vadeTarihi: `${current.getFullYear()}-${pad(current.getMonth() + 1)}-${pad(current.getDate())}`,
          miktar: s.kiraBedeli
        });
      }
      current = new Date(current.getFullYear(), current.getMonth() + 1, s.odemeGunu);
    }
    return eksik;
  }, []);

  const fetchOdemeler = async (tn: string) => {
    try {
      setOdemelerLoading(true);
      const res = await axiosInstance.get(`/api/Odemeler/kira-sozlesme/${tn}`);
      setOdemeler(res.data);
    } catch {
      setOdemeler([]);
    } finally {
      setOdemelerLoading(false);
    }
  };

  const fetchKasalar = async () => {
    try {
      const res = await axiosInstance.get('/api/Kasalar');
      setKasalar(res.data);
    } catch {
      setKasalar([]);
    }
  };

  // Sözleşme ve ödemeler yüklenince eksik ayları hesapla (iptal edilmişlerde gösterme)
  useEffect(() => {
    if (sozlesme && !odemelerLoading) {
      if (!sozlesme.aktifMi) {
        setEksikAylar([]);
        return;
      }
      const eksik = hesaplaEksikAylar(odemeler, sozlesme);
      setEksikAylar(eksik);
      if (eksik.length > 0) {
        setShowEksikUyariModal(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sozlesme, odemeler, odemelerLoading]);

  const fetchSozlesme = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/KiraSozlesmeleri/takip/${takipNo}`);
      setSozlesme(response.data);
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
      navigate('/kiralik/sozlesmeler');
    } finally {
      setLoading(false);
    }
  };

  const fetchDokumanSablonlari = async () => {
    try {
      const response = await axiosInstance.get('/api/DokumanSablonlari');
      setDokumanSablonlari(response.data.filter((s: any) => s.aktifMi));
    } catch (error) {
      console.error('Döküman şablonları yüklenirken hata:', error);
    }
  };

  const handleSablonSec = (sablon: any) => {
    setEditForm({ ...editForm, ekKosullar: sablon.icerik || '' });
    setShowSablonModal(false);
    toast.success(t('msg.islemBasarili'));
  };

  const handleIptalClick = () => {
    setIptalStep(1);
    setShowIptalModal(true);
  };

  const handleIptal = async () => {
    if (!sozlesme) return;
    setIptalSaving(true);
    try {
      // Backend tüm ilişkili ödemeleri (taksitler, Tediye kayıtları, GEL-KRA- gelir kaydı)
      // ve KasaHareketleri'ni otomatik temizler + gayrimenkulü boşa çeker
      await axiosInstance.post(`/api/KiraSozlesmeleri/iptal/${sozlesme.kiraSozlesmeID}`);
      toast.success(t('msg.islemBasarili'));
      setShowIptalModal(false);
      setIptalStep(1);
      fetchSozlesme();
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setIptalSaving(false);
    }
  };

  const handleOpenEdit = () => {
    if (!sozlesme) return;
    setEditForm({
      baslangicTarihi: sozlesme.baslangicTarihi.slice(0, 10),
      bitisTarihi: sozlesme.bitisTarihi.slice(0, 10),
      kiraBedeli: sozlesme.kiraBedeli,
      odemeGunu: sozlesme.odemeGunu,
      kapora: sozlesme.kapora,
      ekKosullar: sozlesme.ekKosullar || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!sozlesme) return;
    setEditSaving(true);
    try {
      const payload = {
        baslangicTarihi: editForm.baslangicTarihi,
        bitisTarihi: editForm.bitisTarihi,
        kiraBedeli: editForm.kiraBedeli,
        odemeGunu: editForm.odemeGunu,
        kapora: editForm.kapora,
        ekKosullar: editForm.ekKosullar
      };
      try {
        await axiosInstance.put(`/api/KiraSozlesmeleri/${sozlesme.kiraSozlesmeID}`, payload);
      } catch {
        await axiosInstance.post(`/api/KiraSozlesmeleri/update/${sozlesme.kiraSozlesmeID}`, payload);
      }
      toast.success(t('msg.islemBasarili'));
      setShowEditModal(false);
      fetchSozlesme();
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setEditSaving(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('tr-TR');

  const ayAdi = (ay: number) => [t('ay.ocak'),t('ay.subat'),t('ay.mart'),t('ay.nisan'),t('ay.mayis'),t('ay.haziran'),t('ay.temmuz'),t('ay.agustos'),t('ay.eylul'),t('ay.ekim'),t('ay.kasim'),t('ay.aralik')][ay];

  const getOdemeTuruVal = (tur: string) => ({ 'Nakit': 0, 'Senet': 1, 'Cek': 2, 'Takas': 3, 'Urun': 4, 'Hizmet': 5 }[tur] ?? 0);
  const getOdemeDurumuVal = (d: string) => d === 'Odendi' ? 1 : 0;

  const handleSaveOdeme = async () => {
    if (!sozlesme) return;
    if (!odemeForm.vadeTarihi || !odemeForm.miktar) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }
    if (odemeForm.odemeDurumu === 'Odendi' && !odemeForm.kasaID) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }
    setOdemeSaving(true);
    try {
      await axiosInstance.post('/api/Odemeler', {
        cariID: sozlesme.cari?.cariID,
        islemTarihi: odemeForm.vadeTarihi,
        vadeTarihi: odemeForm.vadeTarihi,
        tahsilatTarihi: odemeForm.odemeDurumu === 'Odendi' ? odemeForm.vadeTarihi : null,
        miktar: parseFloat(odemeForm.miktar),
        paraBirimiID: 1,
        islemTakipNo: sozlesme.takipNo,
        odemeTuru: getOdemeTuruVal(odemeForm.odemeTuru),
        odemeYonu: 0, // Tahsilat
        odemeDurumu: getOdemeDurumuVal(odemeForm.odemeDurumu),
        kasaID: odemeForm.kasaID ? parseInt(odemeForm.kasaID) : null,
        aciklama: odemeForm.aciklama || '',
        aktifMi: true,
        sozlesmeOdemesi: false,
        kiraSozlesmesiOdemesi: true,
        eklemeTarihi: new Date().toISOString()
      });
      toast.success(t('msg.islemBasarili'));
      setShowOdemeModal(false);
      await fetchOdemeler(sozlesme.takipNo);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setOdemeSaving(false);
    }
  };

  const generatePaymentPlanPDF = () => {
    if (!sozlesme) return;

    const ayIsimleri = [t('ay.ocak'),t('ay.subat'),t('ay.mart'),t('ay.nisan'),t('ay.mayis'),t('ay.haziran'),t('ay.temmuz'),t('ay.agustos'),t('ay.eylul'),t('ay.ekim'),t('ay.kasim'),t('ay.aralik')];

    const aktifOdemeler = odemeler
      .filter(o => o.aktifMi)
      .sort((a, b) => {
        const dateA = a.vadeTarihi ? new Date(a.vadeTarihi).getTime() : 0;
        const dateB = b.vadeTarihi ? new Date(b.vadeTarihi).getTime() : 0;
        return dateA - dateB;
      });

    const toplamTahsilat = aktifOdemeler
      .filter(o => o.odemeDurumu === 'Odendi')
      .reduce((acc, o) => acc + o.miktar, 0);

    const toplamBeklenen = aktifOdemeler.reduce((acc, o) => acc + o.miktar, 0);

    // Build rows separately to avoid nested template literals
    const odemeRows = aktifOdemeler.map((odeme, i) => {
      const vd = odeme.vadeTarihi ? new Date(odeme.vadeTarihi) : null;
      const donem = vd ? ayIsimleri[vd.getMonth()] + ' ' + vd.getFullYear() : '-';
      const vadeTarihiStr = vd ? vd.toLocaleDateString('tr-TR') : '-';
      const miktarStr = odeme.miktar.toLocaleString('tr-TR') + ' ' + (odeme.paraBirimi?.birim || '₺');
      const durumStr = odeme.odemeDurumu === 'Odendi' ? 'Ödendi' : odeme.odemeDurumu;
      return '<tr>' +
        '<td class="text-center">' + (i + 1) + '</td>' +
        '<td class="text-center">' + donem + '</td>' +
        '<td class="text-center">' + vadeTarihiStr + '</td>' +
        '<td class="text-right">' + miktarStr + '</td>' +
        '<td class="text-center">' + odeme.odemeTuru + '</td>' +
        '<td class="text-center">' + durumStr + '</td>' +
        '<td>' + (odeme.aciklama || '-') + '</td>' +
        '</tr>';
    }).join('');

    const odemeTableHtml = aktifOdemeler.length > 0
      ? '<div class="info-section"><h2>Ödeme Detayları</h2><table><thead><tr>' +
        '<th class="text-center">' + t('tablo.sira') + '</th><th class="text-center">Dönem</th><th class="text-center">Vade Tarihi</th>' +
        '<th class="text-right">Miktar</th><th class="text-center">Ödeme Türü</th><th class="text-center">' + t('label.durum') + '</th>' +
        '<th>' + t('label.aciklama') + '</th></tr></thead><tbody>' + odemeRows + '</tbody></table></div>' +
        '<div class="summary">' +
        '<div class="summary-row"><span class="summary-label">Toplam Beklenen:</span><span>' + toplamBeklenen.toLocaleString('tr-TR') + ' ₺</span></div>' +
        '<div class="summary-row"><span class="summary-label">Toplam Tahsilat:</span><span>' + toplamTahsilat.toLocaleString('tr-TR') + ' ₺</span></div>' +
        '<div class="summary-row"><span class="summary-label">Kalan:</span><span>' + (toplamBeklenen - toplamTahsilat).toLocaleString('tr-TR') + ' ₺</span></div>' +
        '</div>'
      : '<p style="text-align:center; color:#999;">Henüz ödeme kaydı bulunmuyor.</p>';

    const raporTarihi = new Date().toLocaleDateString('tr-TR');
    const baslangic = new Date(sozlesme.baslangicTarihi).toLocaleDateString('tr-TR');
    const bitis = new Date(sozlesme.bitisTarihi).toLocaleDateString('tr-TR');
    const kategoriStr = sozlesme.gayrimenkul ? (KATEGORI_MAP[sozlesme.gayrimenkul.kategori] || '-') : '-';

    const printContent = '<!DOCTYPE html><html><head><meta charset="UTF-8">' +
      '<title>' + t('kira.odemePlani') + ' - ' + (sozlesme.cari?.adiSoyadi || '') + '</title>' +
      '<style>' +
      '@media print { @page { size: A4; margin: 1cm; } body { margin: 0; font-family: Arial, sans-serif; font-size: 11pt; } .no-print { display: none; } }' +
      'body { font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.4; color: #333; }' +
      '.header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #333; }' +
      '.header h1 { font-size: 20pt; margin: 0 0 10px 0; font-weight: bold; }' +
      '.two-column-layout { display: flex; gap: 20px; margin-bottom: 20px; }' +
      '.column { flex: 1; }' +
      '.info-section { margin-bottom: 20px; }' +
      '.info-section h2 { font-size: 14pt; font-weight: bold; margin: 0 0 10px 0; padding: 5px 0; border-bottom: 1px solid #666; }' +
      '.info-row { margin: 5px 0; }' +
      '.info-label { font-weight: bold; display: inline-block; min-width: 140px; }' +
      'table { width: 100%; border-collapse: collapse; margin-top: 10px; }' +
      'th { background-color: #333; color: white; padding: 8px; text-align: left; font-weight: bold; }' +
      'td { padding: 8px; border-bottom: 1px solid #ddd; }' +
      'tr:nth-child(even) { background-color: #f9f9f9; }' +
      '.text-right { text-align: right; }' +
      '.text-center { text-align: center; }' +
      '.summary { margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px; }' +
      '.summary-row { display: flex; justify-content: space-between; padding: 4px 0; }' +
      '.summary-label { font-weight: bold; }' +
      '.print-button { position: fixed; top: 20px; right: 20px; padding: 10px 20px; background-color: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 14pt; z-index: 1000; }' +
      '.print-button:hover { background-color: #0056b3; }' +
      '@media screen { body { max-width: 21cm; margin: 1cm auto; padding: 20px; box-shadow: 0 0 10px rgba(0,0,0,0.1); } }' +
      '</style></head><body>' +
      '<button class="print-button no-print" onclick="window.print()">' + t('btn.yazdir') + '</button>' +
      '<div class="header"><h1>KİRA ÖDEME PLANI</h1>' +
      '<p>Takip No: ' + sozlesme.takipNo + ' | Rapor Tarihi: ' + raporTarihi + '</p></div>' +
      '<div class="two-column-layout">' +
      '<div class="column"><div class="info-section"><h2>' + t('kira.kiraciTab') + '</h2>' +
      '<div class="info-row"><span class="info-label">' + t('kira.kiraci') + ':</span><span>' + (sozlesme.cari?.adiSoyadi || '-') + '</span></div>' +
      '<div class="info-row"><span class="info-label">Telefon:</span><span>' + (sozlesme.cari?.telefon1 || '-') + '</span></div>' +
      '<div class="info-row"><span class="info-label">E-posta:</span><span>' + (sozlesme.cari?.ePosta || '-') + '</span></div>' +
      '<div class="info-row"><span class="info-label">TC / VKN:</span><span>' + (sozlesme.cari?.tc || sozlesme.cari?.cariVN || '-') + '</span></div>' +
      '</div></div>' +
      '<div class="column"><div class="info-section"><h2>Gayrimenkul Bilgileri</h2>' +
      '<div class="info-row"><span class="info-label">Gayrimenkul:</span><span>' + (sozlesme.gayrimenkul?.baslik || '-') + '</span></div>' +
      '<div class="info-row"><span class="info-label">Adres:</span><span>' + (sozlesme.gayrimenkul?.adres || '-') + '</span></div>' +
      '<div class="info-row"><span class="info-label">Kategori:</span><span>' + kategoriStr + '</span></div>' +
      '<div class="info-row"><span class="info-label">Kira Bedeli:</span><span>' + sozlesme.kiraBedeli.toLocaleString('tr-TR') + ' ₺ / ay</span></div>' +
      '<div class="info-row"><span class="info-label">Sözleşme:</span><span>' + baslangic + ' - ' + bitis + '</span></div>' +
      '</div></div></div>' +
      odemeTableHtml +
      '<div style="margin-top: 40px; text-align: center; font-size: 9pt; color: #666;">' +
      '<p>' + t('msg.otomatikOlusturuldu') + '.</p></div>' +
      '</body></html>';

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => { printWindow.print(); }, 250);
      };
      toast.success(t('msg.islemBasarili'));
    } else {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const stripHtml = (html: string): string => {
    let text = html.replace(/&nbsp;/gi, ' ');
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/p>/gi, '\n').replace(/<\/div>/gi, '\n').replace(/<\/li>/gi, '\n');
    const tmp = document.createElement('div');
    tmp.innerHTML = text;
    return (tmp.textContent || tmp.innerText || '').replace(/\u00A0/g, ' ');
  };

  const handleSozlesmeIndirPDF = () => {
    if (!sozlesme) return;
    const baslangic = new Date(sozlesme.baslangicTarihi).toLocaleDateString('tr-TR');
    const bitis = new Date(sozlesme.bitisTarihi).toLocaleDateString('tr-TR');
    const kategoriStr = sozlesme.gayrimenkul ? (KATEGORI_MAP[sozlesme.gayrimenkul.kategori] || '-') : '-';
    const maddelerHtml = (sozlesme.ekKosullar || '').replace(/&nbsp;/g, ' ');

    const content = '<!DOCTYPE html><html><head><meta charset="UTF-8">' +
      '<title>' + t('kira.sozlesmeler') + ' - ' + sozlesme.takipNo + '</title>' +
      '<style>' +
      '@media print { @page { size: A4; margin: 1cm; } body { margin: 0; } .no-print { display: none; } }' +
      'body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; line-height: 1.5; color: #1e293b; max-width: 21cm; margin: 1cm auto; padding: 16px; overflow-wrap: break-word; word-wrap: break-word; }' +
      '.header-bar { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); color: #fff; padding: 20px 24px; border-radius: 8px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; }' +
      '.header-bar h1 { font-size: 16pt; font-weight: 700; margin: 0; letter-spacing: 1px; }' +
      '.header-bar .meta { font-size: 8pt; opacity: 0.85; text-align: right; line-height: 1.6; }' +
      '.section { margin-bottom: 16px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }' +
      '.section-title { font-size: 10pt; font-weight: 700; margin: 0; padding: 10px 16px; background: #f1f5f9; color: #334155; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #e2e8f0; }' +
      '.section-title i { font-size: 12px; color: #2563eb; }' +
      '.info-table { width: 100%; border-collapse: collapse; }' +
      '.info-table tr:not(:last-child) td { border-bottom: 1px solid #f1f5f9; }' +
      '.info-table td { padding: 7px 16px; font-size: 10pt; vertical-align: top; }' +
      '.info-table td:first-child { font-weight: 600; width: 160px; color: #475569; background: #fafbfc; }' +
      '.info-table td:last-child { color: #1e293b; }' +
      '.maddeler { padding: 12px 16px; white-space: normal; word-wrap: break-word; overflow-wrap: anywhere; font-size: 10pt; line-height: 1.6; color: #1e293b; }' +
      '.maddeler p { margin: 3px 0; white-space: normal; }' +
      '.maddeler * { max-width: 100%; white-space: normal; overflow-wrap: anywhere; word-wrap: break-word; }' +
      '.signature-area { margin-top: 50px; display: flex; justify-content: space-between; padding: 0 20px; }' +
      '.signature-box { text-align: center; width: 38%; }' +
      '.signature-box .title { font-weight: 700; font-size: 10pt; color: #334155; padding-bottom: 4px; border-bottom: 2px solid #2563eb; margin-bottom: 50px; }' +
      '.signature-box .name { font-size: 9pt; color: #64748b; }' +
      '.footer { margin-top: 30px; text-align: center; font-size: 7pt; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 10px; }' +
      '.print-button { position: fixed; top: 16px; right: 16px; padding: 8px 18px; background: #2563eb; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 11pt; font-weight: 600; z-index: 1000; box-shadow: 0 2px 8px rgba(37,99,235,0.3); }' +
      '.print-button:hover { background: #1d4ed8; }' +
      '@media screen { body { box-shadow: 0 0 20px rgba(0,0,0,0.08); border-radius: 4px; } }' +
      '</style></head><body>' +
      '<button class="print-button no-print" onclick="window.print()">${t(\'btn.yazdir\')}38; Yazdır / PDF</button>' +
      '<div class="header-bar"><h1>KİRA SÖZLEŞMESİ</h1><div class="meta">Takip No: ' + sozlesme.takipNo + '<br>Tarih: ' + new Date().toLocaleDateString('tr-TR') + '</div></div>' +
      '<div class="section"><div class="section-title"><i class="fas fa-building"></i>Gayrimenkul Bilgileri</div>' +
      '<table class="info-table">' +
      '<tr><td>Gayrimenkul</td><td>' + (sozlesme.gayrimenkul?.baslik || '-') + '</td></tr>' +
      '<tr><td>Adres</td><td>' + (sozlesme.gayrimenkul?.adres || '-') + '</td></tr>' +
      '<tr><td>Kategori</td><td>' + kategoriStr + '</td></tr>' +
      '<tr><td>Metrekare</td><td>' + (sozlesme.gayrimenkul?.metrekare || '-') + ' m²</td></tr>' +
      (sozlesme.gayrimenkul?.proje ? '<tr><td>Proje</td><td>' + sozlesme.gayrimenkul.proje.projeAdi + '</td></tr>' : '') +
      '</table></div>' +
      '<div class="section"><div class="section-title"><i class="fas fa-user"></i>' + t('kira.kiraciTab') + '</div>' +
      '<table class="info-table">' +
      '<tr><td>Ad Soyad / Firma</td><td>' + (sozlesme.cari?.adiSoyadi || '-') + (sozlesme.cari?.firmaAdi ? ' (' + sozlesme.cari.firmaAdi + ')' : '') + '</td></tr>' +
      '<tr><td>Telefon</td><td>' + (sozlesme.cari?.telefon1 || '-') + '</td></tr>' +
      '<tr><td>E-posta</td><td>' + (sozlesme.cari?.ePosta || '-') + '</td></tr>' +
      '<tr><td>TC / VKN</td><td>' + (sozlesme.cari?.tc || sozlesme.cari?.cariVN || '-') + '</td></tr>' +
      '<tr><td>Adres</td><td>' + (sozlesme.cari?.adres || '-') + '</td></tr>' +
      '</table></div>' +
      '<div class="section"><div class="section-title"><i class="fas fa-file-contract"></i>Sözleşme Detayları</div>' +
      '<table class="info-table">' +
      '<tr><td>${t(\'kira.baslangic\')}</td><td>' + baslangic + '</td></tr>' +
      '<tr><td>${t(\'kira.bitis\')}</td><td>' + bitis + '</td></tr>' +
      '<tr><td>Ödeme Günü</td><td>Her ayın ' + sozlesme.odemeGunu + '. günü</td></tr>' +
      '<tr><td>Kira Bedeli</td><td>' + sozlesme.kiraBedeli.toLocaleString('tr-TR') + ' ₺</td></tr>' +
      '<tr><td>Kapora</td><td>' + sozlesme.kapora.toLocaleString('tr-TR') + ' ₺</td></tr>' +
      '</table></div>' +
      (maddelerHtml ? '<div class="section"><div class="section-title"><i class="fas fa-clipboard-list"></i>Sözleşme Maddeleri</div><div class="maddeler">' + maddelerHtml + '</div></div>' : '') +
      '<div class="signature-area">' +
      '<div class="signature-box"><div class="title">Kiraya Veren</div><div class="name">&nbsp;</div></div>' +
      '<div class="signature-box"><div class="title">Kiracı</div><div class="name">' + (sozlesme.cari?.adiSoyadi || '') + '</div></div>' +
      '</div>' +
      '<div class="footer">Bu belge ' + new Date().toLocaleDateString('tr-TR') + ' tarihinde otomatik olarak oluşturulmuştur.</div>' +
      '</body></html>';

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(content);
      printWindow.document.close();
      printWindow.onload = () => { setTimeout(() => { printWindow.print(); }, 250); };
      toast.success(t('msg.islemBasarili'));
    } else {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleSozlesmeIndirWord = async () => {
    if (!sozlesme) return;
    const baslangic = new Date(sozlesme.baslangicTarihi).toLocaleDateString('tr-TR');
    const bitis = new Date(sozlesme.bitisTarihi).toLocaleDateString('tr-TR');
    const kategoriStr = sozlesme.gayrimenkul ? (KATEGORI_MAP[sozlesme.gayrimenkul.kategori] || '-') : '-';
    const maddelerText = sozlesme.ekKosullar ? stripHtml(sozlesme.ekKosullar) : '';

    const makeLabelValueRow = (label: string, value: string) => {
      return new TableRow({
        children: [
          new TableCell({
            width: { size: 3000, type: WidthType.DXA },
            children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, size: 22, font: 'Times New Roman' })] })],
          }),
          new TableCell({
            width: { size: 6500, type: WidthType.DXA },
            children: [new Paragraph({ children: [new TextRun({ text: value, size: 22, font: 'Times New Roman' })] })],
          }),
        ],
      });
    };

    const makeTable = (rows: TableRow[]) => {
      return new Table({
        width: { size: 9500, type: WidthType.DXA },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
          left: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
          right: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
          insideVertical: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
        },
        rows,
      });
    };

    const sectionTitle = (text: string) => new Paragraph({
      spacing: { before: 300, after: 100 },
      children: [new TextRun({ text, bold: true, size: 26, font: 'Times New Roman' })],
      heading: HeadingLevel.HEADING_2,
    });

    const children: (Paragraph | Table)[] = [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
        children: [new TextRun({ text: 'KİRA SÖZLEŞMESİ', bold: true, size: 36, font: 'Times New Roman' })],
      }),

      sectionTitle('Gayrimenkul Bilgileri'),
      makeTable([
        makeLabelValueRow('Gayrimenkul', sozlesme.gayrimenkul?.baslik || '-'),
        makeLabelValueRow('Adres', sozlesme.gayrimenkul?.adres || '-'),
        makeLabelValueRow('Kategori', kategoriStr),
        makeLabelValueRow('Metrekare', (sozlesme.gayrimenkul?.metrekare || '-') + ' m²'),
        ...(sozlesme.gayrimenkul?.proje ? [makeLabelValueRow('Proje', sozlesme.gayrimenkul.proje.projeAdi)] : []),
      ]),

      sectionTitle('Kiracı Bilgileri'),
      makeTable([
        makeLabelValueRow('Ad Soyad / Firma', (sozlesme.cari?.adiSoyadi || '-') + (sozlesme.cari?.firmaAdi ? ' (' + sozlesme.cari.firmaAdi + ')' : '')),
        makeLabelValueRow('Telefon', sozlesme.cari?.telefon1 || '-'),
        makeLabelValueRow('E-posta', sozlesme.cari?.ePosta || '-'),
        makeLabelValueRow('TC / VKN', sozlesme.cari?.tc || sozlesme.cari?.cariVN || '-'),
        makeLabelValueRow('Adres', sozlesme.cari?.adres || '-'),
      ]),

      sectionTitle('Sözleşme Detayları'),
      makeTable([
        makeLabelValueRow('Başlangıç Tarihi', baslangic),
        makeLabelValueRow('Bitiş Tarihi', bitis),
        makeLabelValueRow('Ödeme Günü', 'Her ayın ' + sozlesme.odemeGunu + '. günü'),
        makeLabelValueRow('Kira Bedeli', sozlesme.kiraBedeli.toLocaleString('tr-TR') + ' ₺'),
        makeLabelValueRow('Kapora', sozlesme.kapora.toLocaleString('tr-TR') + ' ₺'),
      ]),
    ];

    if (maddelerText) {
      children.push(sectionTitle('Sözleşme Maddeleri'));
      maddelerText.split('\n').filter(line => line.trim()).forEach(line => {
        children.push(new Paragraph({
          spacing: { after: 80 },
          children: [new TextRun({ text: line.trim(), size: 22, font: 'Times New Roman' })],
        }));
      });
    }

    // İmza alanı
    children.push(new Paragraph({ spacing: { before: 600 }, children: [] }));
    children.push(new Table({
      width: { size: 9500, type: WidthType.DXA },
      borders: {
        top: { style: BorderStyle.NONE, size: 0 },
        bottom: { style: BorderStyle.NONE, size: 0 },
        left: { style: BorderStyle.NONE, size: 0 },
        right: { style: BorderStyle.NONE, size: 0 },
        insideHorizontal: { style: BorderStyle.NONE, size: 0 },
        insideVertical: { style: BorderStyle.NONE, size: 0 },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              width: { size: 4750, type: WidthType.DXA },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [new TextRun({ text: 'Kiraya Veren', bold: true, size: 24, font: 'Times New Roman' })],
                }),
                new Paragraph({ spacing: { before: 600 }, children: [] }),
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [new TextRun({ text: '___________________', size: 22, font: 'Times New Roman' })],
                }),
              ],
            }),
            new TableCell({
              width: { size: 4750, type: WidthType.DXA },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [new TextRun({ text: 'Kiracı', bold: true, size: 24, font: 'Times New Roman' })],
                }),
                new Paragraph({ spacing: { before: 600 }, children: [] }),
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [new TextRun({ text: sozlesme.cari?.adiSoyadi || '___________________', size: 22, font: 'Times New Roman' })],
                }),
              ],
            }),
          ],
        }),
      ],
    }));

    try {
      const doc = new Document({
        styles: {
          default: {
            document: {
              run: {
                font: 'Times New Roman',
                language: { value: 'tr-TR' },
              },
            },
          },
        },
        sections: [{ children }],
      });
      const blob = await Packer.toBlob(doc);
      saveAs(blob, 'Kira_Sozlesmesi_' + sozlesme.takipNo + '.docx');
      toast.success(t('msg.islemBasarili'));
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleOpenTopluPlanla = () => {
    if (!sozlesme) return;
    setTopluPlanlaForm({
      toplamTutar: '',
      baslamaTarihi: sozlesme.baslangicTarihi.slice(0, 10),
      taksitSayisi: '',
      aralikAy: '1',
      odemeTuru: 'Nakit',
      aciklama: ''
    });
    setShowTopluPlanlaModal(true);
  };

  const handleSaveTopluPlanla = async () => {
    if (!sozlesme) return;
    const { toplamTutar, baslamaTarihi, taksitSayisi, aralikAy, odemeTuru, aciklama } = topluPlanlaForm;
    if (!toplamTutar || !baslamaTarihi || !taksitSayisi || !aralikAy) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }
    const sayi = parseInt(taksitSayisi);
    const tutar = parseFloat(toplamTutar.replace(/[^0-9.]/g, ''));
    const aralik = parseInt(aralikAy);
    if (sayi <= 0 || tutar <= 0 || aralik <= 0) { toast.error(t('msg.zorunluAlanlar')); return; }
    const toOT = (tur: string): number => ({ Nakit: 0, Senet: 1, Cek: 2, Takas: 3 } as Record<string, number>)[tur] ?? 0;
    const taksitTutari = tutar / sayi;
    let kalanTutar = tutar;
    try {
      setTopluPlanlaLoading(true);
      const promises = [];
      for (let i = 0; i < sayi; i++) {
        const vade = new Date(baslamaTarihi);
        vade.setMonth(vade.getMonth() + i * aralik);
        const miktarBu = i === sayi - 1 ? Math.round(kalanTutar * 100) / 100 : Math.round(taksitTutari * 100) / 100;
        if (i < sayi - 1) kalanTutar -= miktarBu;
        const donemAdi = `${ayAdi(vade.getMonth())} ${vade.getFullYear()}`;
        promises.push(axiosInstance.post('/api/Odemeler', {
          cariID: sozlesme.cari?.cariID,
          islemTarihi: baslamaTarihi,
          vadeTarihi: vade.toISOString().split('T')[0],
          tahsilatTarihi: null,
          miktar: miktarBu,
          paraBirimiID: 1,
          islemTakipNo: sozlesme.takipNo,
          odemeTuru: toOT(odemeTuru),
          odemeYonu: 0,
          odemeDurumu: 0,
          kasaID: null,
          aciklama: `${i + 1}. Taksit - Kira${aciklama ? ' · ' + aciklama : ''} (${donemAdi})`,
          aktifMi: true,
          sozlesmeOdemesi: false,
          kiraSozlesmesiOdemesi: true,
          eklemeTarihi: new Date().toISOString()
        }));
      }
      await Promise.all(promises);
      toast.success(t('msg.islemBasarili'));
      setShowTopluPlanlaModal(false);
      await fetchOdemeler(sozlesme.takipNo);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setTopluPlanlaLoading(false);
    }
  };

  const handleOpenTahsilat = (odeme: KiraOdeme) => {
    const today = new Date().toISOString().split('T')[0];
    setTahsilatTaksit(odeme);
    setTahsilatForm({ tarih: today, kasaID: '', odemeTuru: 'Nakit', aciklama: odeme.aciklama || '' });
    setShowTahsilatModal(true);
  };

  const handleSaveTahsilat = async () => {
    if (!tahsilatTaksit || !sozlesme) return;
    if (!tahsilatForm.kasaID) { toast.error(t('msg.zorunluAlanlar')); return; }
    try {
      setTahsilatLoading(true);
      const toOT = (tur: string): number => ({ Nakit: 0, Senet: 1, Cek: 2, Takas: 3 } as Record<string, number>)[tur] ?? 0;
      // 1. Taksiti Odendi yap
      await axiosInstance.put(`/api/Odemeler/${tahsilatTaksit.odemeID}`, {
        odemeID: tahsilatTaksit.odemeID,
        cariID: tahsilatTaksit.cariID,
        islemTarihi: tahsilatTaksit.islemTarihi,
        vadeTarihi: tahsilatTaksit.vadeTarihi,
        tahsilatTarihi: tahsilatForm.tarih,
        miktar: tahsilatTaksit.miktar,
        paraBirimiID: tahsilatTaksit.paraBirimiID,
        islemTakipNo: tahsilatTaksit.islemTakipNo,
        odemeTuru: toOT(tahsilatForm.odemeTuru),
        odemeYonu: 0,
        odemeDurumu: 1,
        kasaID: parseInt(tahsilatForm.kasaID),
        aciklama: tahsilatTaksit.aciklama,
        aktifMi: true,
        sozlesmeOdemesi: false,
        kiraSozlesmesiOdemesi: true,
        eklemeTarihi: tahsilatTaksit.eklemeTarihi
      });
      // 2. Tediye makbuz kaydı oluştur
      const vd = tahsilatTaksit.vadeTarihi ? new Date(tahsilatTaksit.vadeTarihi) : null;
      const donemAdi = vd ? `${ayAdi(vd.getMonth())} ${vd.getFullYear()} Dönemi` : 'Kira Ödemesi';
      const baseAciklama = tahsilatForm.aciklama || `Kira Ödemesi - ${donemAdi}`;
      await axiosInstance.post('/api/Odemeler', {
        cariID: tahsilatTaksit.cariID,
        islemTarihi: tahsilatForm.tarih,
        vadeTarihi: tahsilatForm.tarih,
        tahsilatTarihi: tahsilatForm.tarih,
        miktar: tahsilatTaksit.miktar,
        paraBirimiID: 1,
        islemTakipNo: tahsilatTaksit.islemTakipNo,
        odemeTuru: toOT(tahsilatForm.odemeTuru),
        odemeYonu: 0,
        odemeDurumu: 1,
        kasaID: parseInt(tahsilatForm.kasaID),
        aciklama: `Tediye · ${baseAciklama}`,
        aktifMi: true,
        sozlesmeOdemesi: false,
        kiraSozlesmesiOdemesi: true,
        eklemeTarihi: new Date().toISOString()
      });
      toast.success(t('msg.islemBasarili'));
      setShowTahsilatModal(false);
      setTahsilatTaksit(null);
      await fetchOdemeler(sozlesme.takipNo);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setTahsilatLoading(false);
    }
  };

  const handleTahsilatGeriAl = async (odeme: KiraOdeme) => {
    if (!sozlesme) return;
    if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
    try {
      setOdemeSaving(true);
      const tediyeAciklama = `Tediye · ${odeme.aciklama || ''}`;
      const tediyeKayit = odemeler.find(o => o.aciklama === tediyeAciklama && o.cariID === odeme.cariID && o.miktar === odeme.miktar);
      if (tediyeKayit) {
        try { await axiosInstance.delete(`/api/Odemeler/${tediyeKayit.odemeID}`); } catch { await axiosInstance.post(`/api/Odemeler/delete/${tediyeKayit.odemeID}`); }
      }
      const toOT = (tur: string): number => ({ Nakit: 0, Senet: 1, Cek: 2, Takas: 3 } as Record<string, number>)[tur] ?? 0;
      await axiosInstance.put(`/api/Odemeler/${odeme.odemeID}`, {
        odemeID: odeme.odemeID, cariID: odeme.cariID,
        islemTarihi: odeme.islemTarihi, vadeTarihi: odeme.vadeTarihi,
        tahsilatTarihi: null, miktar: odeme.miktar, paraBirimiID: odeme.paraBirimiID,
        islemTakipNo: odeme.islemTakipNo, odemeTuru: toOT(odeme.odemeTuru),
        odemeYonu: 0, odemeDurumu: 0, kasaID: null,
        aciklama: odeme.aciklama, aktifMi: true, sozlesmeOdemesi: false,
        kiraSozlesmesiOdemesi: true, eklemeTarihi: odeme.eklemeTarihi
      });
      toast.success(t('msg.islemBasarili'));
      await fetchOdemeler(sozlesme.takipNo);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setOdemeSaving(false);
    }
  };

  const handleTopluEkle = async () => {
    if (!sozlesme || eksikAylar.length === 0) return;
    if (eksikAylar.some(e => getOdemeDurumuVal('Bekliyor') === 1 && !odemeForm.kasaID)) {
      // burası Bekliyor için geçerli değil, ama ileride Ödendi eklenebilir
    }
    setOdemeSaving(true);
    try {
      const baslangic = new Date(sozlesme.baslangicTarihi);
      let firstPayment = new Date(baslangic.getFullYear(), baslangic.getMonth(), sozlesme.odemeGunu || 1);
      if (firstPayment < baslangic) {
        firstPayment = new Date(firstPayment.getFullYear(), firstPayment.getMonth() + 1, sozlesme.odemeGunu || 1);
      }
      const firstYear = firstPayment.getFullYear();
      const firstMonth = firstPayment.getMonth();
      for (const ea of eksikAylar) {
        const taksitNo = (ea.yil - firstYear) * 12 + (ea.ay - firstMonth) + 1;
        await axiosInstance.post('/api/Odemeler', {
          cariID: sozlesme.cari?.cariID,
          islemTarihi: ea.vadeTarihi,
          vadeTarihi: ea.vadeTarihi,
          tahsilatTarihi: null,
          miktar: ea.miktar,
          paraBirimiID: 1,
          islemTakipNo: sozlesme.takipNo,
          odemeTuru: 0, // Nakit
          odemeYonu: 0,
          odemeDurumu: 0, // Bekliyor
          kasaID: null,
          aciklama: `${taksitNo}. Taksit - Kira (${ayAdi(ea.ay)} ${ea.yil})`,
          aktifMi: true,
          sozlesmeOdemesi: false,
          kiraSozlesmesiOdemesi: true,
          eklemeTarihi: new Date().toISOString()
        });
      }
      toast.success(t('msg.islemBasarili'));
      setShowEksikUyariModal(false);
      await fetchOdemeler(sozlesme.takipNo);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setOdemeSaving(false);
    }
  };

  const handleOpenEditOdeme = (odeme: KiraOdeme) => {
    setEditingOdeme(odeme);
    setEditOdemeForm({
      odemeDurumu: odeme.odemeDurumu,
      tahsilatTarihi: odeme.tahsilatTarihi ? odeme.tahsilatTarihi.slice(0, 10) : (odeme.vadeTarihi ? odeme.vadeTarihi.slice(0, 10) : ''),
      kasaID: odeme.kasaID ? String(odeme.kasaID) : '',
      aciklama: odeme.aciklama || ''
    });
    setShowEditOdemeModal(true);
  };

  const handleSaveEditOdeme = async () => {
    if (!editingOdeme || !sozlesme) return;
    if (editOdemeForm.odemeDurumu === 'Odendi' && !editOdemeForm.kasaID) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }
    setOdemeSaving(true);
    try {
      const payload = {
        odemeID: editingOdeme.odemeID,
        cariID: editingOdeme.cariID,
        islemTarihi: editingOdeme.islemTarihi,
        vadeTarihi: editingOdeme.vadeTarihi,
        tahsilatTarihi: editOdemeForm.odemeDurumu === 'Odendi' ? editOdemeForm.tahsilatTarihi : null,
        miktar: editingOdeme.miktar,
        paraBirimiID: editingOdeme.paraBirimiID,
        islemTakipNo: editingOdeme.islemTakipNo,
        odemeTuru: getOdemeTuruVal(editingOdeme.odemeTuru),
        odemeYonu: 0,
        odemeDurumu: getOdemeDurumuVal(editOdemeForm.odemeDurumu),
        kasaID: editOdemeForm.kasaID ? parseInt(editOdemeForm.kasaID) : null,
        aciklama: editOdemeForm.aciklama,
        aktifMi: true,
        sozlesmeOdemesi: false,
        kiraSozlesmesiOdemesi: true,
        eklemeTarihi: editingOdeme.eklemeTarihi
      };
      try {
        await axiosInstance.put(`/api/Odemeler/${editingOdeme.odemeID}`, payload);
      } catch {
        await axiosInstance.post(`/api/Odemeler/update/${editingOdeme.odemeID}`, payload);
      }
      toast.success(t('msg.islemBasarili'));
      setShowEditOdemeModal(false);
      setEditingOdeme(null);
      await fetchOdemeler(sozlesme.takipNo);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setOdemeSaving(false);
    }
  };

  const handleDeleteOdeme = async (odemeID: number) => {
    if (!sozlesme) return;
    if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
    try {
      try {
        await axiosInstance.delete(`/api/Odemeler/${odemeID}`);
      } catch {
        await axiosInstance.post(`/api/Odemeler/delete/${odemeID}`);
      }
      toast.success(t('msg.islemBasarili'));
      await fetchOdemeler(sozlesme.takipNo);
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const isAktif = sozlesme ? sozlesme.aktifMi && new Date(sozlesme.bitisTarihi) >= new Date() : false;
  const isSuresiDolmus = sozlesme ? sozlesme.aktifMi && new Date(sozlesme.bitisTarihi) < new Date() : false;

  const getDurumInfo = () => {
    if (!sozlesme) return { label: '—', color: '#64748b', bg: '#f1f5f9' };
    if (!sozlesme.aktifMi) return { label: t('kira.iptalEdilmis'), color: '#dc2626', bg: '#fef2f2' };
    if (isSuresiDolmus) return { label: t('kira.suresiDolmus'), color: '#ca8a04', bg: '#fffbeb' };
    return { label: t('kira.aktif'), color: '#15803d', bg: '#f0fdf4' };
  };

  const durumInfo = getDurumInfo();

  // Süre hesaplama
  const calcSure = () => {
    if (!sozlesme) return '—';
    const start = new Date(sozlesme.baslangicTarihi);
    const end = new Date(sozlesme.bitisTarihi);
    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    const months = Math.floor(diffDays / 30);
    const days = diffDays % 30;
    if (months > 0 && days > 0) return `${months} ${t('kira.ay')} ${days} ${t('kira.gun')}`;
    if (months > 0) return `${months} ${t('kira.ay')}`;
    return `${diffDays} ${t('kira.gun')}`;
  };

  // Kalan gün
  const calcKalanGun = () => {
    if (!sozlesme) return 0;
    const now = new Date();
    const end = new Date(sozlesme.bitisTarihi);
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  if (loading) {
    return (
      <>
        <style>{`
          .ksd-wrap { background: #f8fafc; min-height: 100vh; padding: 28px 32px; font-family: 'Inter', 'Segoe UI', sans-serif; }
          @keyframes ksd-spin { to { transform: rotate(360deg); } }
          .ksd-spinner { width: 32px; height: 32px; border: 3px solid #f1f5f9; border-top-color: #3b82f6; border-radius: 50%; animation: ksd-spin 0.8s linear infinite; margin: 80px auto 14px; }
          .ksd-loading-text { font-size: 14px; color: #94a3b8; font-weight: 500; text-align: center; }
        `}</style>
        <div className="ksd-wrap">
          <div className="ksd-spinner" />
          <div className="ksd-loading-text">{t('kira.sozlesmeYukleniyor')}</div>
        </div>
      </>
    );
  }

  if (!sozlesme) return null;

  // --- Quick Stats hesaplamaları ---
  const qToplam = odemeler.reduce((s, o) => s + o.miktar, 0);
  const qKalan = odemeler.filter(o => o.odemeDurumu !== 'Odendi').reduce((s, o) => s + o.miktar, 0);
  const qNextPayment = odemeler
    .filter(o => o.odemeDurumu !== 'Odendi' && o.vadeTarihi && new Date(o.vadeTarihi) >= new Date())
    .sort((a, b) => new Date(a.vadeTarihi!).getTime() - new Date(b.vadeTarihi!).getTime())[0];

  return (
    <>
      <style>{`
        .ksd-page { background: rgba(249,250,251,0.5); min-height: 100vh; padding: 28px 32px; font-family: 'Inter', 'Segoe UI', sans-serif; }
        /* keep ksd-wrap for loading state */
        .ksd-wrap { background: rgba(249,250,251,0.5); min-height: 100vh; padding: 28px 32px; font-family: 'Inter', 'Segoe UI', sans-serif; }
        .ksd-page-header { margin-bottom: 24px; display: flex; align-items: flex-start; justify-content: space-between; }
        .ksd-page-title { font-size: 22px; font-weight: 700; color: #0f172a; letter-spacing: -0.4px; margin: 0 0 4px 0; }
        .ksd-page-breadcrumb { font-size: 13px; color: #6b7280; margin-bottom: 4px; }
        .ksd-page-breadcrumb .sep { margin: 0 6px; }
        .ksd-page-breadcrumb .current { color: #1a1a1a; font-weight: 500; }
        .ksd-page-breadcrumb a { color: #6b7280; text-decoration: none; }
        .ksd-page-breadcrumb a:hover { color: #cd6906; text-decoration: underline; }
        .ksd-header-actions { display: flex; gap: 10px; align-items: flex-start; padding-top: 4px; }
        .ksd-btn-back { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 9px 18px; font-size: 13px; color: #64748b; cursor: pointer; font-family: inherit; font-weight: 500; transition: all 0.15s; display: flex; align-items: center; gap: 7px; }
        .ksd-btn-back:hover { border-color: #cbd5e1; color: #0f172a; background: #f8fafc; }
        .ksd-btn-iptal { background: #fff; border: 1.5px solid #ef4444; border-radius: 10px; padding: 9px 18px; font-size: 13px; color: #ef4444; cursor: pointer; font-family: inherit; font-weight: 600; transition: all 0.15s; display: flex; align-items: center; gap: 7px; }
        .ksd-btn-iptal:hover { background: #ef4444; color: #fff; }
        .ksd-status-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; border-radius: 20px; padding: 5px 14px; margin-top: 6px; }
        .ksd-status-dot { width: 7px; height: 7px; border-radius: 50%; }
        /* Stat Cards */
        .ksd-stat-row { display: flex; gap: 16px; margin-bottom: 24px; }
        .ksd-stat-card { flex: 1; background: #fff; border-radius: 14px; border: 1.5px solid #f1f5f9; box-shadow: 0 1px 4px rgba(15,23,42,0.05); padding: 18px 22px; display:flex; align-items: center; gap: 16px; }
        .ksd-stat-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
        .ksd-stat-num { font-size: 22px; font-weight: 700; color: #0f172a; line-height: 1; }
        .ksd-stat-label { font-size: 11px; color: #94a3b8; margin-top: 4px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; }
        /* Info Sections */
        .ksd-card { background: #fff; border-radius: 14px; border: 1.5px solid #f1f5f9; box-shadow: 0 1px 4px rgba(15,23,42,0.05); overflow: hidden; margin-bottom: 20px; }
        .ksd-compact-info { display: grid; grid-template-columns: 1fr 1px 1fr 1px 1fr; gap: 0; }
        .ksd-compact-col { padding: 18px 22px; }
        .ksd-compact-divider { background: #f1f5f9; align-self: stretch; }
        .ksd-compact-col-title { display: flex; align-items: center; gap: 7px; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 12px; }
        .ksd-compact-col-title i { font-size: 12px; }
        .ksd-field { display: flex; align-items: baseline; gap: 6px; margin-bottom: 7px; }
        .ksd-field:last-child { margin-bottom: 0; }
        .ksd-field-label { font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.3px; flex: 0 0 33.333%; white-space: nowrap; }
        .ksd-field-label::after { content: ':'; }
        .ksd-field-value { font-size: 13px; font-weight: 500; color: #0f172a; line-height: 1.4; flex: 0 0 66.666%; }
        .ksd-field-value a { color: #cd6906; text-decoration: none; font-weight: 600; }
        .ksd-field-value a:hover { text-decoration: underline; }
        .ksd-ek-kosullar { margin-bottom: 20px; background: #fff; border-radius: 14px; border: 1.5px solid #f1f5f9; padding: 14px 22px; max-width: 100%; box-sizing: border-box; }
        .ksd-ek-kosullar summary { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; cursor: pointer; list-style: none; display: flex; align-items: center; gap: 6px; }
        .ksd-ek-kosullar summary::-webkit-details-marker { display: none; }
        .ksd-ek-kosullar[open] summary i.fa-chevron-right { transform: rotate(90deg); }
        .ksd-ek-kosullar summary i.fa-chevron-right { transition: transform 0.2s; }
        .ksd-ek-kosullar-content { font-size: 13px; color: #0f172a; line-height: 1.6; margin: 10px 0 0 0; overflow: hidden; white-space: normal; word-wrap: break-word; overflow-wrap: anywhere; }
        .ksd-ek-kosullar-content * { max-width: 100% !important; white-space: normal !important; overflow-wrap: anywhere !important; word-wrap: break-word !important; }
        .ksd-ek-kosullar-content p, .ksd-ek-kosullar-content div, .ksd-ek-kosullar-content span, .ksd-ek-kosullar-content li { white-space: normal !important; }
        /* Modal */
        .ksd-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15,23,42,0.4); backdrop-filter: blur(4px); z-index: 9999; display: flex; align-items: center; justify-content: center; }
        .ksd-modal { background: #fff; border-radius: 16px; box-shadow: 0 25px 50px rgba(0,0,0,0.15); width: 95%; max-width: 440px; }
        .ksd-modal-header { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; }
        .ksd-modal-title { font-size: 17px; font-weight: 700; color: #0f172a; }
        .ksd-modal-body { padding: 24px; }
        .ksd-modal-body p { font-size: 14px; color: #475569; margin: 0 0 8px 0; line-height: 1.5; }
        .ksd-modal-footer { padding: 16px 24px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 10px; }
        .ksd-btn-cancel { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 10px 24px; font-size: 13px; color: #64748b; cursor: pointer; font-family: inherit; font-weight: 500; }
        .ksd-btn-cancel:hover { border-color: #cbd5e1; color: #0f172a; }
        .ksd-btn-danger { background: #ef4444; border: none; border-radius: 10px; padding: 10px 24px; font-size: 13px; color: #fff; cursor: pointer; font-family: inherit; font-weight: 600; transition: background 0.15s; }
        .ksd-btn-danger:hover { background: #dc2626; }
        /* Responsive */
        @media (max-width: 900px) {
          .ksd-stat-row { flex-wrap: wrap; }
          .ksd-stat-card { flex: 1 1 calc(50% - 8px); }
          .ksd-page-header { flex-direction: column; gap: 12px; }
          .ksd-compact-info { grid-template-columns: 1fr; }
          .ksd-compact-divider { display: none; }
        }
        @media (max-width: 600px) {
          .ksd-wrap { padding: 16px 12px; }
          .ksd-stat-card { flex: 1 1 100%; }
        }
        /* Edit Modal */
        .ksd-modal-wide { 
          max-width: 1100px; 
          max-height: 90vh; 
          display: flex; 
          flex-direction: column; 
        }
        .ksd-modal-wide .ksd-modal-header {
          flex-shrink: 0;
        }
        .ksd-modal-wide .ksd-modal-body {
          flex: 1;
          overflow-y: auto;
        }
        .ksd-modal-wide .ksd-modal-footer {
          flex-shrink: 0;
        }
        .ksd-modal-wide .ksd-modal-body::-webkit-scrollbar {
          width: 10px;
        }
        .ksd-modal-wide .ksd-modal-body::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .ksd-modal-wide .ksd-modal-body::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .ksd-modal-wide .ksd-modal-body::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .ksd-form-group-header { 
          display: flex; 
          align-items: center; 
          justify-content: space-between; 
          margin-bottom: 6px; 
        }
        .ksd-sablon-btn {
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 8px;
          padding: 6px 12px;
          font-size: 12px;
          color: #0ea5e9;
          cursor: pointer;
          font-family: inherit;
          font-weight: 600;
          transition: all 0.15s;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ksd-sablon-btn:hover {
          background: #0ea5e9;
          color: #fff;
          border-color: #0ea5e9;
        }
        .ksd-sablon-list {
          max-height: 400px;
          overflow-y: auto;
        }
        .ksd-sablon-item {
          padding: 14px 18px;
          border-bottom: 1px solid #f1f5f9;
          cursor: pointer;
          transition: background 0.15s;
        }
        .ksd-sablon-item:hover {
          background: #f8fafc;
        }
        .ksd-sablon-item:last-child {
          border-bottom: none;
        }
        .ksd-sablon-title {
          font-size: 14px;
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 4px;
        }
        .ksd-sablon-preview {
          font-size: 12px;
          color: #64748b;
          line-height: 1.4;
          max-height: 32px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ksd-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .ksd-form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
        .ksd-form-label { font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; }
        .ksd-form-control { border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 9px 12px; font-size: 14px; font-family: inherit; color: #0f172a; outline: none; transition: border-color 0.15s; width: 100%; box-sizing: border-box; }
        .ksd-form-control:focus { border-color: #3b82f6; }
        .ksd-form-textarea { resize: vertical; min-height: 120px; }
        .ksd-btn-save { background: #3b82f6; border: none; border-radius: 10px; padding: 10px 24px; font-size: 13px; color: #fff; cursor: pointer; font-family: inherit; font-weight: 600; transition: background 0.15s; }
        .ksd-btn-save:hover { background: #2563eb; }
        .ksd-btn-save:disabled { background: #93c5fd; cursor: not-allowed; }
        .ksd-btn-edit { background: #fff; border: 1.5px solid #3b82f6; border-radius: 10px; padding: 9px 18px; font-size: 13px; color: #3b82f6; cursor: pointer; font-family: inherit; font-weight: 600; transition: all 0.15s; display: flex; align-items: center; gap: 7px; }
        .ksd-btn-edit:hover { background: #3b82f6; color: #fff; }
        .ksd-islemler-wrap { position: relative; }
        .ksd-btn-islemler { background: #fff; border: 1.5px solid #6366f1; border-radius: 10px; padding: 9px 18px; font-size: 13px; color: #6366f1; cursor: pointer; font-family: inherit; font-weight: 600; transition: all 0.15s; display: flex; align-items: center; gap: 7px; }
        .ksd-btn-islemler:hover { background: #6366f1; color: #fff; }
        .ksd-islemler-menu { position: absolute; top: calc(100% + 6px); right: 0; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.12); min-width: 220px; z-index: 100; padding: 6px 0; animation: ksd-fadeIn 0.15s ease; }
        @keyframes ksd-fadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .ksd-islemler-menu-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 10px 16px; font-size: 13px; font-weight: 500; font-family: inherit; border: none; background: none; cursor: pointer; color: #334155; transition: background 0.12s; }
        .ksd-islemler-menu-item:hover { background: #f1f5f9; }
        .ksd-islemler-menu-item.danger { color: #ef4444; }
        .ksd-islemler-menu-item.danger:hover { background: #fef2f2; }
        .ksd-islemler-divider { height: 1px; background: #f1f5f9; margin: 4px 0; }
        /* Ödemeler */
        .ksd-btn-green { background: #fff; border: 1.5px solid #16a34a; border-radius: 10px; padding: 9px 18px; font-size: 13px; color: #16a34a; cursor: pointer; font-family: inherit; font-weight: 600; transition: all 0.15s; display: flex; align-items: center; gap: 7px; }
        .ksd-btn-green:hover { background: #16a34a; color: #fff; }
        .ksd-odemeler-section { margin-bottom: 24px; }
        .ksd-odeme-stats { display: flex; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
        .ksd-odeme-stat { flex: 1; min-width: 120px; background: #fff; border-radius: 12px; border: 1.5px solid #f1f5f9; padding: 14px 18px; }
        .ksd-odeme-stat-val { font-size: 18px; font-weight: 700; color: #0f172a; }
        .ksd-odeme-stat-label { font-size: 11px; color: #94a3b8; margin-top: 2px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; }
        .ksd-odeme-table-wrap { overflow-x: auto; border-radius: 12px; border: 1.5px solid #f1f5f9; background: #fff; }
        .ksd-odeme-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .ksd-odeme-table th { background: #f8fafc; padding: 10px 14px; text-align: left; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #f1f5f9; }
        .ksd-odeme-table td { padding: 11px 14px; border-bottom: 1px solid #f8fafc; vertical-align: middle; }
        .ksd-odeme-table tbody tr:hover { background: #f8fafc; }
        .ksd-odeme-table tbody tr:last-child td { border-bottom: none; }
        .ksd-badge { display: inline-flex; align-items: center; gap: 5px; border-radius: 20px; padding: 3px 10px; font-size: 12px; font-weight: 600; }
        .ksd-badge-green { background: #dcfce7; color: #15803d; }
        .ksd-badge-yellow { background: #fef9c3; color: #a16207; }
        .ksd-badge-red { background: #fee2e2; color: #b91c1c; }
        .ksd-btn-icon { background: none; border: none; cursor: pointer; padding: 5px; border-radius: 6px; transition: background 0.15s; font-size: 13px; }
        .ksd-btn-icon:hover { background: #f1f5f9; }
        .ksd-eksik-list { max-height: 260px; overflow-y: auto; border: 1px solid #f1f5f9; border-radius: 8px; }
        .ksd-eksik-row { display: flex; align-items: center; justify-content: space-between; padding: 8px 14px; border-bottom: 1px solid #f8fafc; font-size: 13px; }
        .ksd-eksik-row:last-child { border-bottom: none; }
        .ksd-modal-scroll { max-height: 80vh; overflow-y: auto; }
        .ksd-btn-warning { background: #f59e0b; border: none; border-radius: 10px; padding: 10px 24px; font-size: 13px; color: #fff; cursor: pointer; font-family: inherit; font-weight: 600; transition: background 0.15s; }
        .ksd-btn-warning:hover { background: #d97706; }
        .ksd-btn-warning:disabled { background: #fcd34d; cursor: not-allowed; }
        
        /* React Quill Styles */
        .ksd-form-group .quill {
          background: #fff;
          border-radius: 9px;
          border: 1.5px solid #e2e8f0;
        }
        .ksd-form-group .quill:focus-within {
          border-color: #cd6906;
          box-shadow: 0 0 0 3px rgba(205,105,6,0.12);
        }
        .ksd-form-group .ql-toolbar {
          border: none;
          border-bottom: 1.5px solid #e2e8f0;
          border-radius: 9px 9px 0 0;
          background: #f8fafc;
        }
        .ksd-form-group .ql-container {
          border: none;
          font-size: 14px;
          font-family: inherit;
          min-height: 200px;
          max-height: 350px;
          overflow-y: auto;
        }
        .ksd-form-group .ql-editor {
          min-height: 200px;
          max-height: 350px;
          padding: 14px;
        }
        .ksd-form-group .ql-editor.ql-blank::before {
          color: #94a3b8;
          font-style: normal;
        }
        .ksd-form-group .ql-snow .ql-stroke {
          stroke: #64748b;
        }
        .ksd-form-group .ql-snow .ql-fill {
          fill: #64748b;
        }
        .ksd-form-group .ql-snow .ql-picker-label {
          color: #64748b;
        }
        .ksd-form-group .ql-toolbar button:hover,
        .ksd-form-group .ql-toolbar button:focus,
        .ksd-form-group .ql-toolbar button.ql-active {
          color: #cd6906;
        }
        .ksd-form-group .ql-toolbar button:hover .ql-stroke,
        .ksd-form-group .ql-toolbar button:focus .ql-stroke,
        .ksd-form-group .ql-toolbar button.ql-active .ql-stroke {
          stroke: #cd6906;
        }
        .ksd-form-group .ql-toolbar button:hover .ql-fill,
        .ksd-form-group .ql-toolbar button:focus .ql-fill,
        .ksd-form-group .ql-toolbar button.ql-active .ql-fill {
          fill: #cd6906;
        }
        .ksd-form-group .ql-container::-webkit-scrollbar {
          width: 10px;
        }
        .ksd-form-group .ql-container::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 5px;
          margin: 4px;
        }
        .ksd-form-group .ql-container::-webkit-scrollbar-thumb {
          background: #94a3b8;
          border-radius: 5px;
          border: 2px solid #f8fafc;
        }
        .ksd-form-group .ql-container::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }
      `}</style>

      <div className="ksd-page">
        {showPrintPreview && sozlesme ? (() => {
          const baslangic = new Date(sozlesme.baslangicTarihi).toLocaleDateString('tr-TR');
          const bitis = new Date(sozlesme.bitisTarihi).toLocaleDateString('tr-TR');
          const kategoriStr = sozlesme.gayrimenkul ? (KATEGORI_MAP[sozlesme.gayrimenkul.kategori] || '-') : '-';
          const thStyle: React.CSSProperties = { padding: '7px 12px', fontWeight: 700, fontSize: 10, color: '#374151', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left', letterSpacing: '0.3px', textTransform: 'uppercase' };
          const tdLabelStyle: React.CSSProperties = { padding: '6px 12px', fontWeight: 600, fontSize: 10.5, color: '#4b5563', width: '38%', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };
          const tdValueStyle: React.CSSProperties = { padding: '6px 12px', fontSize: 10.5, color: '#111827', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };
          return (
            <>
              {/* Preview toolbar */}
              <div className="ksd-print-toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <button
                  onClick={() => setShowPrintPreview(false)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 13, fontWeight: 500, color: '#6b7280' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#111827')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
                >
                  <ArrowLeft size={15} />
                  {t('kira.sozlesmeDetayinaDon')}
                </button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => handleSozlesmeIndirWord()}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #d1d5db', color: '#374151', background: '#fff', padding: '7px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                  >
                    <FileDown size={14} />
                    {t('btn.wordIndir')}
                  </button>
                  <button
                    onClick={handlePdfIndir}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #d1d5db', color: '#374151', background: '#fff', padding: '7px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                  >
                    <FileDown size={14} />
                    {t('btn.pdfIndir')}
                  </button>
                  <button
                    onClick={() => window.print()}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#111827', color: '#fff', border: 'none', padding: '7px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#1f2937')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#111827')}
                  >
                    <Printer size={14} />
                    {t('btn.yazdir')}
                  </button>
                </div>
              </div>

              {/* A4 Paper */}
              <style>{`
                @media print {
                  @page { size: A4; margin: 0; }
                  .ksd-print-toolbar { display: none !important; }
                  .ksd-page { background: white !important; padding: 0 !important; }
                  .ksd-print-paper { box-shadow: none !important; border: none !important; margin: 0 !important; max-width: 100% !important; padding: 15mm 18mm !important; border-radius: 0 !important; }
                }
              `}</style>
              <div
                ref={printPaperRef}
                className="ksd-print-paper"
                style={{ background: '#fff', maxWidth: 780, margin: '0 auto', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', border: '1px solid #e5e7eb', borderRadius: 4, padding: '36px 40px', fontFamily: 'Georgia, "Times New Roman", serif', color: '#111827', lineHeight: 1.6, boxSizing: 'border-box' }}
              >
                {/* Document Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 20, marginBottom: 24, borderBottom: '2px solid #111827' }}>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#111827', letterSpacing: '1px', fontFamily: 'Georgia, serif', textTransform: 'uppercase' }}>Kira Sözleşmesi</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, fontFamily: 'Arial, sans-serif' }}>Kiralık Taşınmaz Kira Sözleşmesi</div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 11, color: '#374151', lineHeight: 1.8, fontFamily: 'Arial, sans-serif' }}>
                    <div><span style={{ color: '#6b7280' }}>Sözleşme No:</span> <strong>{sozlesme.takipNo}</strong></div>
                    <div><span style={{ color: '#6b7280' }}>Düzenlenme Tarihi:</span> <strong>{new Date().toLocaleDateString('tr-TR')}</strong></div>
                    <div><span style={{ color: '#6b7280' }}>Durum:</span> <strong>{sozlesme.aktifMi ? 'Aktif' : 'Feshedilmiş'}</strong></div>
                  </div>
                </div>

                {/* Taraflar 2 col */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, marginBottom: 20, border: '1px solid #d1d5db' }}>
                  <div>
                    <div style={{ ...thStyle, borderRight: '1px solid #d1d5db', borderBottom: '1px solid #d1d5db' }}>Kiraya Veren</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, sans-serif' }}>
                      <tbody>
                        {sozlesme.gayrimenkul?.proje && (
                          <tr>
                            <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6' }}>Proje</td>
                            <td style={tdValueStyle}>{sozlesme.gayrimenkul.proje.projeAdi}</td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6' }}>Yetkili</td>
                          <td style={tdValueStyle}>—</td>
                        </tr>
                        <tr>
                          <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6', borderBottom: 'none' }}>İletişim</td>
                          <td style={{ ...tdValueStyle, borderBottom: 'none' }}>—</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div style={{ borderLeft: '1px solid #d1d5db' }}>
                    <div style={{ ...thStyle, borderBottom: '1px solid #d1d5db' }}>Kiracı</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, sans-serif' }}>
                      <tbody>
                        <tr>
                          <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6' }}>Ad Soyad</td>
                          <td style={tdValueStyle}>{sozlesme.cari?.adiSoyadi || '—'}</td>
                        </tr>
                        {sozlesme.cari?.firmaAdi && (
                          <tr>
                            <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6' }}>Firma</td>
                            <td style={tdValueStyle}>{sozlesme.cari.firmaAdi}</td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6' }}>T.C. / V.K.N.</td>
                          <td style={tdValueStyle}>{sozlesme.cari?.tc || sozlesme.cari?.cariVN || '—'}</td>
                        </tr>
                        <tr>
                          <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6' }}>Telefon</td>
                          <td style={tdValueStyle}>{sozlesme.cari?.telefon1 || '—'}</td>
                        </tr>
                        <tr>
                          <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6', borderBottom: 'none' }}>Adres</td>
                          <td style={{ ...tdValueStyle, borderBottom: 'none' }}>{sozlesme.cari?.adres || '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Taşınmaz */}
                <div style={{ border: '1px solid #d1d5db', marginBottom: 20, fontFamily: 'Arial, sans-serif' }}>
                  <div style={thStyle}>Kiralanan Taşınmaz</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr><td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6' }}>Başlık</td><td style={tdValueStyle}>{sozlesme.gayrimenkul?.baslik || '—'}</td></tr>
                        <tr><td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6' }}>Kategori</td><td style={tdValueStyle}>{kategoriStr}</td></tr>
                        <tr><td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6', borderBottom: 'none' }}>Metrekare</td><td style={{ ...tdValueStyle, borderBottom: 'none' }}>{sozlesme.gayrimenkul?.metrekare ? sozlesme.gayrimenkul.metrekare + ' m²' : '—'}</td></tr>
                      </tbody>
                    </table>
                    <table style={{ width: '100%', borderCollapse: 'collapse', borderLeft: '1px solid #e5e7eb' }}>
                      <tbody>
                        <tr><td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6' }}>Adres</td><td style={tdValueStyle}>{sozlesme.gayrimenkul?.adres || '—'}</td></tr>
                        <tr><td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6' }}>Kira Başlangıcı</td><td style={tdValueStyle}>{baslangic}</td></tr>
                        <tr><td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6', borderBottom: 'none' }}>Kira Bitişi</td><td style={{ ...tdValueStyle, borderBottom: 'none' }}>{bitis}</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sözleşme Koşulları */}
                <div style={{ border: '1px solid #d1d5db', marginBottom: 20, fontFamily: 'Arial, sans-serif' }}>
                  <div style={thStyle}>Sözleşme Koşulları</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6', width: '20%' }}>Aylık Kira</td>
                        <td style={{ ...tdValueStyle, fontWeight: 700, width: '30%' }}>{sozlesme.kiraBedeli.toLocaleString('tr-TR')} ₺</td>
                        <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6', width: '20%' }}>Ödeme Günü</td>
                        <td style={tdValueStyle}>Her ayın {sozlesme.odemeGunu}. günü</td>
                      </tr>
                      <tr>
                        <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6', borderBottom: 'none' }}>Depozito</td>
                        <td style={{ ...tdValueStyle, borderBottom: 'none' }}>{sozlesme.kapora.toLocaleString('tr-TR')} ₺</td>
                        <td style={{ ...tdLabelStyle, borderRight: '1px solid #f3f4f6', borderBottom: 'none' }}>Kira Süresi</td>
                        <td style={{ ...tdValueStyle, borderBottom: 'none' }}>{baslangic} – {bitis}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Ek Koşullar */}
                {sozlesme.ekKosullar && (
                  <div style={{ border: '1px solid #d1d5db', marginBottom: 20, fontFamily: 'Arial, sans-serif' }}>
                    <div style={thStyle}>Sözleşme Maddeleri ve Ek Koşullar</div>
                    <div
                      style={{ padding: '14px 16px', fontSize: 11, lineHeight: 1.7, color: '#1f2937' }}
                      dangerouslySetInnerHTML={{ __html: sozlesme.ekKosullar.replace(/&nbsp;/g, ' ') }}
                    />
                  </div>
                )}

                {/* İmza */}
                <div style={{ marginTop: 56, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, fontFamily: 'Arial, sans-serif', fontSize: 11 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ height: 56, borderBottom: '1px solid #374151', marginBottom: 10 }}></div>
                    <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>Kiraya Veren</div>
                    <div style={{ color: '#6b7280', fontSize: 10 }}>İmza / Kaşe</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ height: 56, borderBottom: '1px solid #374151', marginBottom: 10 }}></div>
                    <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>Kiracı</div>
                    <div style={{ color: '#6b7280', fontSize: 10 }}>{sozlesme.cari?.adiSoyadi || ''}</div>
                  </div>
                </div>

                {/* Footer */}
                <div style={{ marginTop: 40, paddingTop: 12, borderTop: '1px solid #e5e7eb', textAlign: 'center', fontSize: 9, color: '#9ca3af', fontFamily: 'Arial, sans-serif' }}>
                  Bu belge {new Date().toLocaleDateString('tr-TR')} tarihinde sistem tarafından otomatik olarak oluşturulmuştur. â€¢ {sozlesme.takipNo}
                </div>
              </div>
            </>
          );
        })() : (<>
        {/* â”€â”€ Header â”€â”€ */}
        <div style={{ marginBottom: 24 }}>
          {/* Geri Dön */}
          <button
            onClick={() => navigate('/kiralik/sozlesmeler')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#9ca3af', marginBottom: 8, transition: 'color 0.15s' }}
            onMouseEnter={e => (e.currentTarget.style.color = '#111827')}
            onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
          >
            <ArrowLeft size={15} />
            {t('kira.sozlesmeListesineDon')}
          </button>
          {/* Title + Actions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', letterSpacing: '-0.5px', margin: 0 }}>
              Kiralık Sözleşme Detayı — {sozlesme.takipNo}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {/* Düzenle */}
              <button
                onClick={handleOpenEdit}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #e5e7eb', color: '#374151', background: '#fff', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                <Pencil size={14} />
                {t('btn.duzenle')}
              </button>
              {/* Yazdır */}
              <button
                onClick={() => setShowPrintPreview(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1.5px solid #cd6906', color: '#cd6906', background: '#fff', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fff8f0'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              >
                <Printer size={14} />
                {t('btn.yazdir')}
              </button>
              {/* PDF İndir + Word İndir ikon butonları */}
              <button
                onClick={() => { setPendingAutoDownload(true); setShowPrintPreview(true); }}
                title={t('btn.indir')}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb', color: '#cd6906', background: '#fff', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#fff8f0')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                <FileDown size={16} />
              </button>
              <button
                onClick={() => handleSozlesmeIndirWord()}
                title={t('btn.indir')}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb', color: '#2563eb', background: '#fff', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                <FileText size={16} />
              </button>
              {/* Sözleşmeyi Feshet */}
              {sozlesme.aktifMi && (
                <button
                  onClick={handleIptalClick}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #fca5a5', color: '#ef4444', background: '#fff', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#fef2f2')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >
                  <XCircle size={14} />
                  {t('kira.sozlesmeyiFeshet')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* â”€â”€ Quick Stats â”€â”€ */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {/* Durum */}
          <div style={{ background: '#fff', border: '1px solid #f3f4f6', padding: 20, borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.6px', marginBottom: 6 }}>{t('kira.sozlesmeDurumu')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: durumInfo.color }}>{durumInfo.label}</div>
          </div>
          {/* Toplam Tutar */}
          <div style={{ background: '#fff', border: '1px solid #f3f4f6', padding: 20, borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.6px', marginBottom: 6 }}>{t('kira.toplamTutar')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{formatCurrency(qToplam)}</div>
          </div>
          {/* Kalan Bakiye */}
          <div style={{ background: '#fff', border: '1px solid #f3f4f6', padding: 20, borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.6px', marginBottom: 6 }}>{t('kira.kalanBakiye')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: qKalan > 0 ? '#d97706' : '#16a34a' }}>{formatCurrency(qKalan)}</div>
          </div>
          {/* Sonraki Ödeme */}
          <div style={{ background: '#fff', border: '1px solid #f3f4f6', padding: 20, borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.6px', marginBottom: 6 }}>{t('kira.sonrakiOdeme')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
              {qNextPayment?.vadeTarihi ? formatDate(qNextPayment.vadeTarihi) : '—'}
            </div>
          </div>
        </div>

        {/* â”€â”€ Bilgi Kartları (3 kolon) â”€â”€ */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>

          {/* Sözleşme Bilgileri */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ background: 'rgba(249,250,251,0.5)', padding: '10px 24px', borderBottom: '1px solid #f3f4f6', fontSize: 13, fontWeight: 700, color: '#374151' }}>
              {t('kira.sozlesmeBilgileri')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 16, columnGap: 32, padding: 24 }}>
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('label.takipNo')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>#{sozlesme.takipNo}</div></div>
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('label.durum')}</div><div style={{ fontSize: 13, fontWeight: 500, color: durumInfo.color }}>{durumInfo.label}</div></div>
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('kira.baslangic')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{formatDate(sozlesme.baslangicTarihi)}</div></div>
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('kira.bitis')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{formatDate(sozlesme.bitisTarihi)}</div></div>
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('kira.kiraBedeli')}</div><div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{formatCurrency(sozlesme.kiraBedeli)}</div></div>
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('kira.odemeGunu')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{t('kira.herAyin')} {sozlesme.odemeGunu}. {t('kira.gunu')}</div></div>
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('kira.kapora')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{formatCurrency(sozlesme.kapora)}</div></div>
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('kira.sure')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{calcSure()}</div></div>
            </div>
          </div>

          {/* Müşteri Bilgileri */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ background: 'rgba(249,250,251,0.5)', padding: '10px 24px', borderBottom: '1px solid #f3f4f6', fontSize: 13, fontWeight: 700, color: '#374151' }}>
              {t('kira.musteriBilgileri')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 16, columnGap: 32, padding: 24 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('label.adSoyad')}</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
                  <a href={`/musteriler/${sozlesme.cari?.cariID}`} onClick={e => { e.preventDefault(); navigate(`/musteriler/${sozlesme.cari?.cariID}`); }} style={{ color: '#cd6906', textDecoration: 'none', fontWeight: 600 }}>
                    {sozlesme.cari?.adiSoyadi || '—'}
                  </a>
                </div>
              </div>
              {sozlesme.cari?.firmaAdi && (
                <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('label.firma')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{sozlesme.cari.firmaAdi}</div></div>
              )}
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('label.telefon')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{sozlesme.cari?.telefon1 || '—'}{sozlesme.cari?.telefon2 ? ` / ${sozlesme.cari.telefon2}` : ''}</div></div>
              {sozlesme.cari?.ePosta && <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('label.ePosta')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{sozlesme.cari.ePosta}</div></div>}
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('label.tur')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{sozlesme.cari?.turu || '—'}</div></div>
              {sozlesme.cari?.tc && <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('label.tcKimlik')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{sozlesme.cari.tc}</div></div>}
              {(sozlesme.cari?.cariVD || sozlesme.cari?.cariVN) && (
                <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('label.vergiDairesiNo')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{sozlesme.cari?.cariVD || '—'} / {sozlesme.cari?.cariVN || '—'}</div></div>
              )}
              {sozlesme.cari?.adres && (
                <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{t('label.adres')}</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{sozlesme.cari.adres}</div></div>
              )}
            </div>
          </div>

          {/* Taşınmaz Bilgileri */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ background: 'rgba(249,250,251,0.5)', padding: '10px 24px', borderBottom: '1px solid #f3f4f6', fontSize: 13, fontWeight: 700, color: '#374151' }}>
              {t('kira.tasinmazBilgileri')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 16, columnGap: 32, padding: 24 }}>
              <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>Başlık</div><div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{sozlesme.gayrimenkul?.baslik || '—'}</div></div>
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>Takip No</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>#{sozlesme.gayrimenkul?.takipNo || '—'}</div></div>
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>Kategori</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{KATEGORI_MAP[sozlesme.gayrimenkul?.kategori ?? 0]}</div></div>
              <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>Alan</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{sozlesme.gayrimenkul?.metrekare ?? 0} m²</div></div>
              {sozlesme.gayrimenkul?.kat && <div><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>Kat</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{sozlesme.gayrimenkul.kat}</div></div>}
              <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>Adres</div><div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{sozlesme.gayrimenkul?.adres || '—'}</div></div>
              {sozlesme.gayrimenkul?.proje && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>Proje</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
                    <a href={`/projeler/${sozlesme.gayrimenkul.proje.projeID}`} onClick={e => { e.preventDefault(); navigate(`/projeler/${sozlesme.gayrimenkul!.proje!.projeID}`); }} style={{ color: '#cd6906', textDecoration: 'none', fontWeight: 600 }}>
                      {sozlesme.gayrimenkul.proje.projeAdi}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Ek Koşullar */}
        {sozlesme.ekKosullar && (
          <details className="ksd-ek-kosullar" style={{ marginBottom: 24 }}>
            <summary>
              <i className="fas fa-chevron-right" style={{ fontSize: 10, color: '#94a3b8' }}></i>
              <i className="fas fa-clipboard-list" style={{ color: '#0ea5e9', marginLeft: 2 }}></i>
              Sözleşme Maddeleri
            </summary>
            <div
              className="ksd-ek-kosullar-content"
              dangerouslySetInnerHTML={{ __html: sozlesme.ekKosullar.replace(/&nbsp;/g, ' ') }}
            />
          </details>
        )}

        {/* â”€â”€ Ödeme Planı â”€â”€ */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
          {/* Tablo Başlık Satırı */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 700, color: '#111827' }}>
              <i className="fas fa-credit-card" style={{ color: '#cd6906', fontSize: 14 }}></i>
              Ödeme Planı
              <span style={{ background: '#f3f4f6', color: '#6b7280', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>{odemeler.length}</span>
              {eksikAylar.length > 0 && (
                <span
                  style={{ background: '#fef3c7', color: '#92400e', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={() => setShowEksikUyariModal(true)}
                >
                  <i className="fas fa-exclamation-triangle" style={{ fontSize: 11 }}></i>
                  {eksikAylar.length} eksik ay
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button className="ksd-btn-green" onClick={generatePaymentPlanPDF} disabled={odemeler.length === 0}>
                <i className="fas fa-download" style={{ fontSize: 11 }}></i>
                Ödeme Planı İndir
              </button>
              <button className="ksd-btn-green" onClick={handleOpenTopluPlanla}>
                <i className="fas fa-list-ol" style={{ fontSize: 11 }}></i>
                Toplu Ödeme Planla
              </button>
              <button className="ksd-btn-green" onClick={() => {
                setOdemeForm({ vadeTarihi: '', miktar: String(sozlesme.kiraBedeli), odemeTuru: 'Nakit', odemeDurumu: 'Bekliyor', kasaID: '', aciklama: '' });
                setShowOdemeModal(true);
              }}>
                <i className="fas fa-plus" style={{ fontSize: 11 }}></i>
                Ödeme Ekle
              </button>
            </div>
          </div>


            {odemelerLoading ? (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <div style={{ width: 28, height: 28, border: '3px solid #f3f4f6', borderTopColor: '#cd6906', borderRadius: '50%', animation: 'ksd-spin 0.8s linear infinite', margin: '0 auto 10px' }} />
                <div style={{ fontSize: 13, color: '#9ca3af' }}>Ödemeler yükleniyor...</div>
              </div>
            ) : odemeler.length === 0 ? (
              <div style={{ padding: '56px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.2 }}>ğŸ’³</div>
                <div style={{ fontSize: 13, color: '#9ca3af', fontWeight: 500 }}>Bu sözleşmeye ait ödeme kaydı bulunmuyor.</div>
                {eksikAylar.length > 0 && (
                  <button className="ksd-btn-warning" style={{ margin: '16px auto 0', display: 'inline-flex', alignItems: 'center', gap: 7 }} onClick={() => setShowEksikUyariModal(true)}>
                    <i className="fas fa-calendar-plus"></i>
                    {eksikAylar.length} aylık ödeme planı oluştur
                  </button>
                )}
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Ay / Dönem</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Vade Tarihi</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Tahsilat</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Taksit Tutarı</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{t('label.tur')}</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{t('label.durum')}</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Kasa</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{t('label.aciklama')}</th>
                        <th style={{ padding: '12px 16px', width: 60 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {odemeler.map((odeme) => {
                        const overdue = odeme.vadeTarihi && odeme.odemeDurumu !== 'Odendi' && new Date(odeme.vadeTarihi) < new Date();
                        const vd = odeme.vadeTarihi ? new Date(odeme.vadeTarihi) : null;
                        const donem = vd ? `${ayAdi(vd.getMonth())} ${vd.getFullYear()}` : '—';
                        return (
                          <tr key={odeme.odemeID} style={{ borderBottom: '1px solid #f3f4f6' }} onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                            <td style={{ padding: '12px 24px', fontWeight: 600, color: '#111827', fontSize: 13 }}>{donem}</td>
                            <td style={{ padding: '12px 16px' }}>
                              {vd ? (
                                <span style={{ fontSize: 13, color: overdue ? '#dc2626' : '#374151', fontWeight: overdue ? 700 : 400 }}>
                                  {vd.toLocaleDateString('tr-TR')}
                                </span>
                              ) : <span style={{ color: '#d1d5db' }}>—</span>}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {odeme.tahsilatTarihi ? (
                                <span style={{ fontSize: 13, color: '#16a34a' }}>
                                  {new Date(odeme.tahsilatTarihi).toLocaleDateString('tr-TR')}
                                </span>
                              ) : <span style={{ color: '#d1d5db' }}>—</span>}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, fontSize: 14, color: '#111827', letterSpacing: '-0.3px' }}>
                              {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(odeme.miktar)}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{ background: '#fff8f0', color: '#cd6906', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{odeme.odemeTuru}</span>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {odeme.odemeDurumu === 'Odendi' ? (
                                <span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>Ödendi</span>
                              ) : overdue ? (
                                <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>Gecikti</span>
                              ) : (
                                <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{t('durum.bekliyor')}</span>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: 12, color: '#6b7280' }}>
                              {odeme.kasa ? odeme.kasa.kasaAdi : <span style={{ color: '#d1d5db' }}>—</span>}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ fontSize: 12, color: '#6b7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={odeme.aciklama}>
                                {odeme.aciklama || <span style={{ color: '#d1d5db' }}>—</span>}
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {odeme.odemeDurumu !== 'Odendi' && (
                                  <button className="ksd-btn-icon" title="Tahsilat Yap" onClick={() => handleOpenTahsilat(odeme)} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
                                    <i className="fas fa-hand-holding-usd" style={{ color: '#16a34a', fontSize: 11 }}></i>
                                  </button>
                                )}
                                {odeme.odemeDurumu === 'Odendi' && /^\d+\.\s*taksit/i.test(odeme.aciklama || '') && (
                                  <button className="ksd-btn-icon" title="Tahsilat Geri Al" onClick={() => handleTahsilatGeriAl(odeme)} style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8 }}>
                                    <i className="fas fa-undo" style={{ color: '#ea580c', fontSize: 11 }}></i>
                                  </button>
                                )}
                                <button className="ksd-btn-icon" title={t('btn.duzenle')} onClick={() => handleOpenEditOdeme(odeme)}>
                                  <i className="fas fa-edit" style={{ color: '#9ca3af' }}></i>
                                </button>
                                <button className="ksd-btn-icon" title={t('btn.sil')} onClick={() => handleDeleteOdeme(odeme.odemeID)}>
                                  <i className="fas fa-trash" style={{ color: '#ef4444' }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Alt Toplamlar */}
                {(() => {
                  const odenenTutar = odemeler.filter(o => o.odemeDurumu === 'Odendi').reduce((s, o) => s + o.miktar, 0);
                  const bekleyenTutar = odemeler.filter(o => o.odemeDurumu !== 'Odendi').reduce((s, o) => s + o.miktar, 0);
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderTop: '2px solid #f3f4f6' }}>
                      <div style={{ padding: '16px 24px', borderRight: '1px solid #f3f4f6' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.6px', marginBottom: 4, textTransform: 'uppercase' }}>Toplam Ödendi</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: '#16a34a' }}>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(odenenTutar)}</div>
                      </div>
                      <div style={{ padding: '16px 24px', borderRight: '1px solid #f3f4f6' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.6px', marginBottom: 4, textTransform: 'uppercase' }}>Toplam Bekleyen</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: '#d97706' }}>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(bekleyenTutar)}</div>
                      </div>
                      <div style={{ padding: '16px 24px' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.6px', marginBottom: 4, textTransform: 'uppercase' }}>Genel Toplam</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: '#111827' }}>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(odenenTutar + bekleyenTutar)}</div>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>

      </>) }{/* end print preview conditional */}
      </div>{/* end ksd-page */}

      {/* Düzenle Modal */}
      {showEditModal && (
        <div className="ksd-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="ksd-modal ksd-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="ksd-modal-header">
              <div className="ksd-modal-title">Sözleşmeyi Düzenle — #{sozlesme?.takipNo}</div>
            </div>
            <div className="ksd-modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div className="ksd-form-group" style={{ marginBottom: 0 }}>
                  <label className="ksd-form-label">Başlangıç Tarihi</label>
                  <TrDateInput className="ksd-form-control"
                    value={editForm.baslangicTarihi}
                    onChange={e => setEditForm({ ...editForm, baslangicTarihi: e.target.value })} />
                </div>
                <div className="ksd-form-group" style={{ marginBottom: 0 }}>
                  <label className="ksd-form-label">Bitiş Tarihi</label>
                  <TrDateInput className="ksd-form-control"
                    value={editForm.bitisTarihi}
                    onChange={e => setEditForm({ ...editForm, bitisTarihi: e.target.value })} />
                </div>
                <div className="ksd-form-group" style={{ marginBottom: 0 }}>
                  <label className="ksd-form-label">Ödeme Günü</label>
                  <input type="number" className="ksd-form-control" min="1" max="31"
                    value={editForm.odemeGunu}
                    onChange={e => setEditForm({ ...editForm, odemeGunu: parseInt(e.target.value) || 1 })} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div className="ksd-form-group" style={{ marginBottom: 0 }}>
                  <label className="ksd-form-label">Kira Bedeli (₺)</label>
                  <input type="text" className="ksd-form-control"
                    value={editForm.kiraBedeli ? editForm.kiraBedeli.toLocaleString('tr-TR') : ''}
                    onChange={e => {
                      const raw = e.target.value.replace(/\./g, '').replace(',', '.');
                      setEditForm({ ...editForm, kiraBedeli: isNaN(parseFloat(raw)) ? 0 : parseFloat(raw) });
                    }} />
                </div>
                <div className="ksd-form-group" style={{ marginBottom: 0 }}>
                  <label className="ksd-form-label">Kapora (₺)</label>
                  <input type="text" className="ksd-form-control"
                    value={editForm.kapora ? editForm.kapora.toLocaleString('tr-TR') : ''}
                    onChange={e => {
                      const raw = e.target.value.replace(/\./g, '').replace(',', '.');
                      setEditForm({ ...editForm, kapora: isNaN(parseFloat(raw)) ? 0 : parseFloat(raw) });
                    }} />
                </div>
              </div>
              <div className="ksd-form-group">
                <div className="ksd-form-group-header">
                  <label className="ksd-form-label" style={{ marginBottom: 0 }}>Sözleşme Maddeleri</label>
                  <button 
                    type="button"
                    className="ksd-sablon-btn"
                    onClick={() => setShowSablonModal(true)}
                  >
                    <i className="fas fa-file-alt"></i>
                    Şablondan Getir
                  </button>
                </div>
                <ReactQuill
                  theme="snow"
                  value={editForm.ekKosullar}
                  onChange={(value) => setEditForm({ ...editForm, ekKosullar: value })}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'indent': '-1'}, { 'indent': '+1' }],
                      ['link'],
                      ['clean']
                    ]
                  }}
                  formats={[
                    'header',
                    'bold', 'italic', 'underline', 'strike',
                    'list', 'bullet', 'indent',
                    'link'
                  ]}
                  placeholder={t('label.ara')}
                />
              </div>
            </div>
            <div className="ksd-modal-footer">
              <button className="ksd-btn-cancel" onClick={() => setShowEditModal(false)}>{t('btn.iptal')}</button>
              <button className="ksd-btn-save" disabled={editSaving} onClick={handleSaveEdit}>
                {editSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* İptal Modal */}
      {showIptalModal && (
        <div className="ksd-modal-overlay" onClick={() => { if (!iptalSaving) { setShowIptalModal(false); setIptalStep(1); } }}>
          <div className="ksd-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="ksd-modal-header" style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
              <div className="ksd-modal-title" style={{ color: '#991b1b', display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-times-circle" style={{ color: '#ef4444' }}></i>
                Sözleşmeyi Feshet
              </div>
            </div>
            <div className="ksd-modal-body">
              <p><strong>#{sozlesme?.takipNo}</strong> numaralı kira sözleşmesini feshetmek istediğinize emin misiniz?</p>
              <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginTop: 12, fontSize: 12.5, color: '#991b1b' }}>
                <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }}></i>
                Bu işlem; tüm kira taksitleri, tahsilat kayıtları ve gelir kayıtları silinecek, gayrimenkulün durumu <strong>"Boş"</strong> olarak güncellenecektir.
              </div>
            </div>
            <div className="ksd-modal-footer">
              <button className="ksd-btn-cancel" onClick={() => setShowIptalModal(false)} disabled={iptalSaving}>{t('btn.iptal')}</button>
              <button className="ksd-btn-danger" onClick={handleIptal} disabled={iptalSaving}>
                {iptalSaving ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Feshediliyor...</> : <><i className="fas fa-times-circle" style={{ marginRight: 6 }}></i>Sözleşmeyi Feshet</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Eksik Aylar Uyarı Modal */}
      {showEksikUyariModal && eksikAylar.length > 0 && (
        <div className="ksd-modal-overlay" onClick={() => setShowEksikUyariModal(false)}>
          <div className="ksd-modal ksd-modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="ksd-modal-header" style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a' }}>
              <div className="ksd-modal-title" style={{ color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
                Eksik Kira Ödemeleri Tespit Edildi
              </div>
            </div>
            <div className="ksd-modal-body ksd-modal-scroll">
              <p style={{ fontSize: 13, color: '#475569', marginBottom: 12, lineHeight: 1.5 }}>
                Sözleşme başlangıcından bugüne kadar <strong>{eksikAylar.length} aylık</strong> kira ödemesi kaydı eksik.
                Aşağıdaki ödemeleri <strong>"Ödenmedi"</strong> durumunda sisteme ekleyebilirsiniz.
              </p>
              <div className="ksd-eksik-list">
                {eksikAylar.map((ea, i) => (
                  <div key={i} className="ksd-eksik-row">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ background: '#fef9c3', color: '#a16207', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
                        {ayAdi(ea.ay)} {ea.yil}
                      </span>
                      <span style={{ fontSize: 12, color: '#64748b' }}>
                        Vade: {new Date(ea.vadeTarihi).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                      {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(ea.miktar)}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', marginTop: 14, fontSize: 12.5, color: '#15803d' }}>
                <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
                Tüm eksik ödemeler <strong>Bekliyor</strong> durumunda eklenecektir. Sonradan her birini ayrı ayrı düzenleyebilirsiniz.
              </div>
            </div>
            <div className="ksd-modal-footer">
              <button className="ksd-btn-cancel" onClick={() => setShowEksikUyariModal(false)} disabled={odemeSaving}>
                Şimdi Değil
              </button>
              <button className="ksd-btn-warning" onClick={handleTopluEkle} disabled={odemeSaving}>
                {odemeSaving
                  ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Ekleniyor...</>
                  : <><i className="fas fa-calendar-plus" style={{ marginRight: 6 }}></i>{eksikAylar.length} Ödeme Planını Oluştur</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toplu Ödeme Planla Modal */}
      {showTopluPlanlaModal && (
        <div className="ksd-modal-overlay" onClick={() => setShowTopluPlanlaModal(false)}>
          <div className="ksd-modal ksd-modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <div className="ksd-modal-header">
              <div className="ksd-modal-title">
                <i className="fas fa-list-ol" style={{ color: '#cd6906', marginRight: 8 }}></i>
                Toplu Ödeme Planla
              </div>
            </div>
            <div className="ksd-modal-body">
              <div className="ksd-form-row" style={{ marginBottom: 16 }}>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Toplam Tutar (₺) *</label>
                  <input type="number" className="ksd-form-control" min="0" step="0.01" placeholder="0.00"
                    value={topluPlanlaForm.toplamTutar}
                    onChange={e => setTopluPlanlaForm({ ...topluPlanlaForm, toplamTutar: e.target.value })} />
                </div>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Başlangıç Tarihi *</label>
                  <TrDateInput className="ksd-form-control"
                    value={topluPlanlaForm.baslamaTarihi}
                    onChange={e => setTopluPlanlaForm({ ...topluPlanlaForm, baslamaTarihi: e.target.value })} />
                </div>
              </div>
              <div className="ksd-form-row" style={{ marginBottom: 16 }}>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Taksit Sayısı *</label>
                  <input type="number" className="ksd-form-control" min="1" step="1" placeholder="12"
                    value={topluPlanlaForm.taksitSayisi}
                    onChange={e => setTopluPlanlaForm({ ...topluPlanlaForm, taksitSayisi: e.target.value })} />
                </div>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Aralık (Ay) *</label>
                  <input type="number" className="ksd-form-control" min="1" step="1"
                    value={topluPlanlaForm.aralikAy}
                    onChange={e => setTopluPlanlaForm({ ...topluPlanlaForm, aralikAy: e.target.value })} />
                </div>
              </div>
              <div className="ksd-form-row" style={{ marginBottom: 16 }}>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Ödeme Türü</label>
                  <select className="ksd-form-control"
                    value={topluPlanlaForm.odemeTuru}
                    onChange={e => setTopluPlanlaForm({ ...topluPlanlaForm, odemeTuru: e.target.value })}>
                    <option value="Nakit">Nakit</option>
                    <option value="Senet">Senet</option>
                    <option value="Cek">{t('odeme.cek')}</option>
                    <option value="Takas">Takas</option>
                  </select>
                </div>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">{t('label.aciklama')}</label>
                  <input type="text" className="ksd-form-control" placeholder={t('label.notlar')}
                    value={topluPlanlaForm.aciklama}
                    onChange={e => setTopluPlanlaForm({ ...topluPlanlaForm, aciklama: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="ksd-modal-footer">
              <button className="ksd-btn-secondary" onClick={() => setShowTopluPlanlaModal(false)} disabled={topluPlanlaLoading}>{t('btn.iptal')}</button>
              <button className="ksd-btn-primary" onClick={handleSaveTopluPlanla} disabled={topluPlanlaLoading}>
                {topluPlanlaLoading ? 'Oluşturuluyor...' : 'Ödeme Planı Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tahsilat Yap Modal */}
      {showTahsilatModal && tahsilatTaksit && (
        <div className="ksd-modal-overlay" onClick={() => setShowTahsilatModal(false)}>
          <div className="ksd-modal ksd-modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="ksd-modal-header">
              <div className="ksd-modal-title">
                <i className="fas fa-hand-holding-usd" style={{ color: '#16a34a', marginRight: 8 }}></i>
                Tahsilat Yap
              </div>
            </div>
            <div className="ksd-modal-body">
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>{tahsilatTaksit.aciklama || 'Ödeme'}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#15803d', marginTop: 4 }}>
                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(tahsilatTaksit.miktar)}
                </div>
              </div>
              <div className="ksd-form-row" style={{ marginBottom: 16 }}>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Tahsilat Tarihi *</label>
                  <TrDateInput className="ksd-form-control"
                    value={tahsilatForm.tarih}
                    onChange={e => setTahsilatForm({ ...tahsilatForm, tarih: e.target.value })} />
                </div>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Kasa *</label>
                  <select className="ksd-form-control"
                    value={tahsilatForm.kasaID}
                    onChange={e => setTahsilatForm({ ...tahsilatForm, kasaID: e.target.value })}>
                    <option value="">Kasa seçin...</option>
                    {kasalar.map(k => (
                      <option key={k.kasaID} value={String(k.kasaID)}>{k.kasaAdi}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="ksd-form-row" style={{ marginBottom: 16 }}>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Ödeme Türü</label>
                  <select className="ksd-form-control"
                    value={tahsilatForm.odemeTuru}
                    onChange={e => setTahsilatForm({ ...tahsilatForm, odemeTuru: e.target.value })}>
                    <option value="Nakit">Nakit</option>
                    <option value="Senet">Senet</option>
                    <option value="Cek">{t('odeme.cek')}</option>
                    <option value="Takas">Takas</option>
                  </select>
                </div>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">{t('label.aciklama')}</label>
                  <input type="text" className="ksd-form-control"
                    value={tahsilatForm.aciklama}
                    onChange={e => setTahsilatForm({ ...tahsilatForm, aciklama: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="ksd-modal-footer">
              <button className="ksd-btn-secondary" onClick={() => setShowTahsilatModal(false)} disabled={tahsilatLoading}>{t('btn.iptal')}</button>
              <button className="ksd-btn-primary" onClick={handleSaveTahsilat} disabled={tahsilatLoading}>
                {tahsilatLoading ? 'Kaydediliyor...' : 'Tahsilatı Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ödeme Ekle Modal */}
      {showOdemeModal && (
        <div className="ksd-modal-overlay" onClick={() => setShowOdemeModal(false)}>
          <div className="ksd-modal ksd-modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="ksd-modal-header">
              <div className="ksd-modal-title">
                <i className="fas fa-plus-circle" style={{ color: '#16a34a', marginRight: 8 }}></i>
                Kira Ödemesi Ekle
              </div>
            </div>
            <div className="ksd-modal-body">
              <div className="ksd-form-row" style={{ marginBottom: 0 }}>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Vade Tarihi *</label>
                  <TrDateInput className="ksd-form-control"
                    value={odemeForm.vadeTarihi}
                    onChange={e => setOdemeForm({ ...odemeForm, vadeTarihi: e.target.value })} />
                </div>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Tutar (₺) *</label>
                  <input type="number" className="ksd-form-control" min="0" step="0.01"
                    value={odemeForm.miktar}
                    onChange={e => setOdemeForm({ ...odemeForm, miktar: e.target.value })} />
                </div>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Ödeme Türü</label>
                  <select className="ksd-form-control" value={odemeForm.odemeTuru} onChange={e => setOdemeForm({ ...odemeForm, odemeTuru: e.target.value })}>
                    <option value="Nakit">Nakit</option>
                    <option value="Senet">Senet</option>
                    <option value="Cek">Ã‡ek</option>
                    <option value="Takas">Takas</option>
                    <option value="Hizmet">Hizmet</option>
                  </select>
                </div>
                <div className="ksd-form-group">
                  <label className="ksd-form-label">Durum</label>
                  <select className="ksd-form-control" value={odemeForm.odemeDurumu} onChange={e => setOdemeForm({ ...odemeForm, odemeDurumu: e.target.value })}>
                    <option value="Bekliyor">Bekliyor</option>
                    <option value="Odendi">Ödendi</option>
                  </select>
                </div>
              </div>
              {odemeForm.odemeDurumu === 'Odendi' && (
                <div className="ksd-form-group" style={{ marginTop: 16 }}>
                  <label className="ksd-form-label">Kasa *</label>
                  <select className="ksd-form-control" value={odemeForm.kasaID} onChange={e => setOdemeForm({ ...odemeForm, kasaID: e.target.value })}>
                    <option value="">Kasa seçin</option>
                    {kasalar.map(k => (
                      <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="ksd-form-group" style={{ marginTop: 16 }}>
                <label className="ksd-form-label">{t('label.aciklama')}</label>
                <input type="text" className="ksd-form-control"
                  placeholder={`${sozlesme?.cari?.adiSoyadi || ''} kira ödemesi`}
                  value={odemeForm.aciklama}
                  onChange={e => setOdemeForm({ ...odemeForm, aciklama: e.target.value })} />
              </div>
            </div>
            <div className="ksd-modal-footer">
              <button className="ksd-btn-cancel" onClick={() => setShowOdemeModal(false)} disabled={odemeSaving}>{t('btn.iptal')}</button>
              <button className="ksd-btn-save" onClick={handleSaveOdeme} disabled={odemeSaving}>
                {odemeSaving
                  ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Kaydediliyor...</>
                  : <><i className="fas fa-save" style={{ marginRight: 6 }}></i>Kaydet</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ödeme Düzenle Modal */}
      {showEditOdemeModal && editingOdeme && (
        <div className="ksd-modal-overlay" onClick={() => { setShowEditOdemeModal(false); setEditingOdeme(null); }}>
          <div className="ksd-modal ksd-modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="ksd-modal-header">
              <div className="ksd-modal-title">
                <i className="fas fa-edit" style={{ color: '#3b82f6', marginRight: 8 }}></i>
                Ödeme Düzenle
                {editingOdeme?.vadeTarihi && (
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#64748b', marginLeft: 8 }}>
                    — {ayAdi(new Date(editingOdeme.vadeTarihi!).getMonth())} {new Date(editingOdeme.vadeTarihi!).getFullYear()}
                  </span>
                )}
              </div>
            </div>
            <div className="ksd-modal-body">
              <div style={{ background: '#f8fafc', border: '1.5px solid #f1f5f9', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>TUTAR</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                    {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(editingOdeme?.miktar ?? 0)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>VADE</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                    {editingOdeme?.vadeTarihi ? new Date(editingOdeme.vadeTarihi).toLocaleDateString('tr-TR') : '—'}
                  </div>
                </div>
              </div>

              <div className="ksd-form-group">
                <label className="ksd-form-label">Ödeme Durumu</label>
                <select className="ksd-form-control" value={editOdemeForm.odemeDurumu} onChange={e => setEditOdemeForm({ ...editOdemeForm, odemeDurumu: e.target.value })}>
                  <option value="Bekliyor">Bekliyor</option>
                  <option value="Odendi">Ödendi</option>
                </select>
              </div>
              {editOdemeForm.odemeDurumu === 'Odendi' && (
                <>
                  <div className="ksd-form-group">
                    <label className="ksd-form-label">Tahsilat Tarihi</label>
                    <TrDateInput className="ksd-form-control"
                      value={editOdemeForm.tahsilatTarihi}
                      onChange={e => setEditOdemeForm({ ...editOdemeForm, tahsilatTarihi: e.target.value })} />
                  </div>
                  <div className="ksd-form-group">
                    <label className="ksd-form-label">Kasa *</label>
                    <select className="ksd-form-control" value={editOdemeForm.kasaID} onChange={e => setEditOdemeForm({ ...editOdemeForm, kasaID: e.target.value })}>
                      <option value="">Kasa seçin</option>
                      {kasalar.map(k => (
                        <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <div className="ksd-form-group">
                <label className="ksd-form-label">{t('label.aciklama')}</label>
                <input type="text" className="ksd-form-control"
                  value={editOdemeForm.aciklama}
                  onChange={e => setEditOdemeForm({ ...editOdemeForm, aciklama: e.target.value })} />
              </div>
            </div>
            <div className="ksd-modal-footer">
              <button className="ksd-btn-cancel" onClick={() => { setShowEditOdemeModal(false); setEditingOdeme(null); }} disabled={odemeSaving}>{t('btn.iptal')}</button>
              <button className="ksd-btn-save" onClick={handleSaveEditOdeme} disabled={odemeSaving}>
                {odemeSaving
                  ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Kaydediliyor...</>
                  : <><i className="fas fa-save" style={{ marginRight: 6 }}></i>Güncelle</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Döküman Şablonları Seçim Modal */}
      {showSablonModal && (
        <div className="ksd-modal-overlay" onClick={() => setShowSablonModal(false)}>
          <div className="ksd-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="ksd-modal-header">
              <div className="ksd-modal-title">
                <i className="fas fa-file-alt" style={{ color: '#0ea5e9', marginRight: 8 }}></i>
                Döküman Şablonu Seç
              </div>
            </div>
            <div className="ksd-modal-body" style={{ padding: 0 }}>
              {dokumanSablonlari.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                  <i className="fas fa-inbox" style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}></i>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>Henüz şablon eklenmemiş</p>
                  <p style={{ margin: '4px 0 0 0', fontSize: 12 }}>Tanımlamalar &rsaquo; Döküman Şablonları menüsünden şablon ekleyebilirsiniz.</p>
                </div>
              ) : (
                <div className="ksd-sablon-list">
                  {dokumanSablonlari.map((sablon: any) => (
                    <div
                      key={sablon.dokumanSablonID}
                      className="ksd-sablon-item"
                      onClick={() => handleSablonSec(sablon)}
                    >
                      <div className="ksd-sablon-title">
                        <i className="fas fa-file-alt" style={{ color: '#0ea5e9', fontSize: 12, marginRight: 6 }}></i>
                        {sablon.sablonBasligi}
                      </div>
                      {sablon.icerik && (
                        <div 
                          className="ksd-sablon-preview"
                          dangerouslySetInnerHTML={{ 
                            __html: sablon.icerik.replace(/<[^>]*>/g, '').substring(0, 100) + '...' 
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="ksd-modal-footer">
              <button className="ksd-btn-cancel" onClick={() => setShowSablonModal(false)}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Sözleşme İndir Modal */}
      {showSozlesmeIndirModal && sozlesme && (
        <div className="ksd-modal-overlay" onClick={() => setShowSozlesmeIndirModal(false)}>
          <div className="ksd-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="ksd-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <i className="fas fa-file-download" style={{ color: '#8b5cf6', fontSize: 16 }}></i>
                <span>Sözleşme İndir</span>
              </div>
            </div>
            <div className="ksd-modal-body" style={{ padding: '24px' }}>
              <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>Kira sözleşmesini indirmek istediğiniz formatı seçin:</p>
              <div style={{ display: 'flex', gap: 14 }}>
                <button
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '24px 16px', border: '1.5px solid #e2e8f0', borderRadius: 12, background: '#fff', cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.background = '#fef2f2'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#fff'; }}
                  onClick={() => { handleSozlesmeIndirPDF(); setShowSozlesmeIndirModal(false); }}
                >
                  <i className="fas fa-file-pdf" style={{ fontSize: 32, color: '#ef4444' }}></i>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>PDF</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>(.pdf)</span>
                </button>
                <button
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '24px 16px', border: '1.5px solid #e2e8f0', borderRadius: 12, background: '#fff', cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#eff6ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#fff'; }}
                  onClick={() => { handleSozlesmeIndirWord(); setShowSozlesmeIndirModal(false); }}
                >
                  <i className="fas fa-file-word" style={{ fontSize: 32, color: '#3b82f6' }}></i>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>WORD</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>(.docx)</span>
                </button>
              </div>
            </div>
            <div className="ksd-modal-footer">
              <button className="ksd-btn-cancel" onClick={() => setShowSozlesmeIndirModal(false)}>{t('btn.kapat')}</button>
            </div>
          </div>
        </div>
      )}

      {/* â”€â”€ end â”€â”€ */}
    </>
  );
};

export default KiraSozlesmesiDetay;

